import { useState, useEffect, Fragment } from "react";
import { supabase } from "../lib/supabase.js";
import {
  updateCaseConcern,
  getCaseVideos,
  uploadCaseVideo,
  deleteCaseVideo,
  getCaseById,
  getThumb,
  getRemoteThumbUrl,
  isMobileLike,
} from "../lib/cases.js";
import {
  markNotificationRead,
  markAllNotificationsRead,
} from "../lib/notifications.js";
import {
  PLAN_PRICE,
  PACKAGE_OPTIONS,
  getTossPayments,
  createPaymentIntent,
  listMyPurchases,
  fetchReceiptUrl,
} from "../lib/payments.js";

// 부모용 리포트는 마크다운을 렌더하지 않고 평문으로 출력한다.
// AI가 무심코 `**볼드**`, `## 헤더`, `- 불릿` 등을 넣으면 사용자에게
// 별 두 개·#·하이픈이 그대로 보이므로, 표시 직전에 떼어낸다.
function stripMarkdown(input) {
  if (input == null) return input;
  let s = String(input);
  // 코드 블록 / 인라인 코드 (백틱은 평문에서도 드문 기호라 통째로 제거)
  s = s.replace(/```[\s\S]*?```/g, (m) => m.replace(/```/g, ""));
  s = s.replace(/`([^`]+)`/g, "$1");
  // 강조: **bold**, __bold__, *em*, _em_  (URL 안 _는 보호)
  s = s.replace(/\*\*([^*\n]+?)\*\*/g, "$1");
  s = s.replace(/__([^_\n]+?)__/g, "$1");
  s = s.replace(/(^|[^*\w])\*([^*\n]+?)\*(?!\w)/g, "$1$2");
  s = s.replace(/(^|[^_\w])_([^_\n]+?)_(?!\w)/g, "$1$2");
  // 헤더(#, ##, ### …): 줄 앞쪽 # 묶음 제거
  s = s.replace(/^\s{0,3}#{1,6}\s+/gm, "");
  // 인용 부호: > quote
  s = s.replace(/^\s{0,3}>\s?/gm, "");
  // 리스트 마커: -, *, +, 1.   (줄 앞쪽만)
  s = s.replace(/^\s{0,3}[-*+]\s+/gm, "");
  s = s.replace(/^\s{0,3}\d+\.\s+/gm, "");
  // 링크 [text](url) → text
  s = s.replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1");
  // 잔여 ** 떨어진 것
  s = s.replace(/\*\*/g, "");
  return s;
}

function timeAgo(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return "방금 전";
  if (mins < 60) return `${mins}분 전`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}시간 전`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}일 전`;
  return new Date(iso).toISOString().slice(0, 10).replace(/-/g, ".");
}

const NOTIF_EMOJI = {
  report_ready: "💌",
  case_received: "📥",
  case_accepted: "📥",
  payment_ok: "💳",
};

// Compute friendly age like "3세 2개월" or "8개월" from birth_month date.
function formatAge(birthMonth) {
  if (!birthMonth) return "";
  const b = new Date(birthMonth);
  const n = new Date();
  const months =
    (n.getFullYear() - b.getFullYear()) * 12 + (n.getMonth() - b.getMonth());
  if (months < 12) return `${Math.max(0, months)}개월`;
  const years = Math.floor(months / 12);
  const rem = months % 12;
  return rem === 0 ? `${years}세` : `${years}세 ${rem}개월`;
}

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}.${m}.${day}`;
}

// ————————————————————————————————————————————————————
// 0. Splash / Login — Google OAuth + Email/Password + 비번 찾기
// ————————————————————————————————————————————————————
const INPUT_STYLE = {
  width: "100%",
  padding: "14px 16px",
  borderRadius: 12,
  border: "1px solid #e2e8f0",
  fontSize: 15,
  fontFamily: "inherit",
  background: "#fff",
};

function GoogleLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path d="M17.64 9.2c0-.64-.06-1.25-.17-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" fill="#4285F4"/>
      <path d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.83.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z" fill="#34A853"/>
      <path d="M3.97 10.71A5.41 5.41 0 0 1 3.68 9c0-.6.1-1.17.28-1.71V4.96H.96A9 9 0 0 0 0 9c0 1.45.35 2.82.96 4.04l3.01-2.33z" fill="#FBBC05"/>
      <path d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 0 0 9 0 9 9 0 0 0 .96 4.96l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
  );
}

function KakaoLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#191600" d="M12 3C6.94 3 2.84 6.2 2.84 10.15c0 2.55 1.7 4.79 4.27 6.06-.19.69-.68 2.48-.78 2.87-.12.48.18.47.37.34.15-.1 2.39-1.62 3.36-2.28.63.09 1.28.14 1.94.14 5.06 0 9.16-3.2 9.16-7.15S17.06 3 12 3z"/>
    </svg>
  );
}

function NaverLogo() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#fff" d="M16.27 12.84 7.42 0H0v24h7.73V11.16L16.58 24H24V0h-7.73v12.84z"/>
    </svg>
  );
}

// 네이버 커스텀 OAuth 플로우(서버리스 함수 + 콘솔 Callback URL + Vercel env) 준비 완료.
// 콘솔 Callback URL 추가 + Vercel env(NAVER_*) 입력이 끝나면 true 로 켠다.
// (카카오는 Supabase 네이티브라 별도 설정 없이 바로 동작)
const NAVER_LOGIN_ENABLED = true;

// /api/auth/naver-callback 이 실패 시 붙이는 ?auth_error=<code> → 한국어 메시지
const NAVER_ERROR_MESSAGES = {
  naver_not_configured: "네이버 로그인이 아직 준비 중이에요. 다른 방법으로 로그인해주세요.",
  naver_supabase_env: "서버 설정 문제로 네이버 로그인에 실패했어요. 잠시 후 다시 시도해주세요.",
  naver_params: "네이버 로그인 응답이 올바르지 않아요. 다시 시도해주세요.",
  naver_state: "보안 확인에 실패했어요(세션 만료). 다시 로그인해주세요.",
  naver_token: "네이버 인증에 실패했어요. 다시 시도해주세요.",
  naver_profile: "네이버 프로필을 불러오지 못했어요. 다시 시도해주세요.",
  naver_no_email: "네이버 계정에서 이메일 제공에 동의해야 로그인할 수 있어요.",
  naver_create: "계정 생성 중 문제가 발생했어요. 잠시 후 다시 시도해주세요.",
  naver_link: "로그인 링크 발급에 실패했어요. 잠시 후 다시 시도해주세요.",
  naver_exception: "네이버 로그인 중 문제가 발생했어요. 잠시 후 다시 시도해주세요.",
};

export function ScreenLogin() {
  const [mode, setMode] = useState("login"); // 'login' | 'signup' | 'forgot'
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);

  const reset = () => { setError(null); setInfo(null); };

  // 네이버 콜백이 ?auth_error=... 로 되돌아왔으면 메시지로 표시하고 URL 을 정리
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("auth_error");
    if (!code) return;
    setError(NAVER_ERROR_MESSAGES[code] || "로그인 중 문제가 발생했어요. 다시 시도해주세요.");
    params.delete("auth_error");
    const qs = params.toString();
    const clean = window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash;
    window.history.replaceState({}, "", clean);
  }, []);

  const signInGoogle = async () => {
    reset();
    setLoading(true);
    const { error: err } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: window.location.origin },
    });
    if (err) {
      setError(err.message);
      setLoading(false);
    }
    // success path redirects away; no need to unset loading
  };

  // 카카오는 Supabase 네이티브 지원 — 책방과 같은 Supabase 인스턴스라
  // provider 가 이미 활성화되어 있어 구글과 동일하게 동작한다.
  const signInKakao = async () => {
    reset();
    setLoading(true);
    const { error: err } = await supabase.auth.signInWithOAuth({
      provider: "kakao",
      options: { redirectTo: window.location.origin },
    });
    if (err) {
      setError(err.message);
      setLoading(false);
    }
    // success path redirects away
  };

  // 네이버는 Supabase 네이티브 미지원 → 커스텀 OAuth 서버리스 플로우로 전체 페이지 이동.
  // /api/auth/naver-start 가 CSRF state 발급 후 네이버 인증 페이지로 redirect 하고,
  // /api/auth/naver-callback 이 magic-link action_link 로 되돌려보내 세션을 확립한다.
  const signInNaver = () => {
    reset();
    setLoading(true);
    window.location.href = "/api/auth/naver-start";
  };

  const friendly = (msg) => {
    if (!msg) return msg;
    if (/invalid login credentials/i.test(msg)) return "이메일 또는 비밀번호가 맞지 않아요";
    if (/email not confirmed/i.test(msg)) return "이메일 인증이 필요해요. 메일함을 확인해주세요";
    if (/user already registered/i.test(msg)) return "이미 가입된 이메일이에요. 로그인해주세요";
    if (/password should be at least/i.test(msg)) return "비밀번호는 6자 이상이어야 해요";
    if (/rate limit/i.test(msg)) return "잠시 후 다시 시도해주세요";
    return msg;
  };

  const submit = async () => {
    reset();
    const em = email.trim().toLowerCase();
    if (!em.includes("@")) { setError("이메일을 확인해주세요"); return; }

    if (mode === "forgot") {
      setLoading(true);
      const { error: err } = await supabase.auth.resetPasswordForEmail(em, {
        redirectTo: window.location.origin,
      });
      setLoading(false);
      if (err) setError(friendly(err.message));
      else setInfo("비밀번호 재설정 링크를 보냈어요. 메일함을 확인해주세요.");
      return;
    }

    if (!password) { setError("비밀번호를 입력해주세요"); return; }

    if (mode === "signup") {
      if (password.length < 6) { setError("비밀번호는 6자 이상이어야 해요"); return; }
      if (password !== confirmPw) { setError("비밀번호 확인이 일치하지 않아요"); return; }
      setLoading(true);
      const { data, error: err } = await supabase.auth.signUp({
        email: em, password,
      });
      setLoading(false);
      if (err) { setError(friendly(err.message)); return; }
      if (!data.session) {
        setInfo("확인 메일을 보냈어요. 메일 속 링크를 눌러 가입을 완료해주세요.");
      }
      // else: auto-logged-in; AuthProvider picks up the session.
      return;
    }

    // mode === 'login'
    setLoading(true);
    const { error: err } = await supabase.auth.signInWithPassword({
      email: em, password,
    });
    setLoading(false);
    if (err) setError(friendly(err.message));
  };

  return (
    <div className="screen warm-gradient" style={{display:'flex',flexDirection:'column'}}>
      <div className="status-spacer"/>
      <div style={{flex:1,display:'flex',flexDirection:'column',justifyContent:'center',padding:'0 28px'}}>
        <div style={{fontSize:38,marginBottom:10}}>📖</div>
        <div style={{fontSize:24,fontWeight:800,letterSpacing:-0.5,lineHeight:1.25,color:'#1a1a2e',marginBottom:8}}>
          우리 아이의 작은 몸짓,<br/>전문가가 함께 읽어드립니다
        </div>
        <div style={{fontSize:13,color:'#4a5568',lineHeight:1.7,marginBottom:16}}>
          영상 몇 개만 올려주세요. 25년차 소아물리치료사가 직접 보고 정리해드려요.
        </div>
        <div style={{display:'flex',alignItems:'center',gap:10,background:'#2c5f8d10',padding:'8px 12px',borderRadius:12,marginBottom:18}}>
          <span style={{fontSize:16}}>🏥</span>
          <div style={{fontSize:11,color:'#2c5f8d',fontWeight:700,lineHeight:1.4}}>
            키즈피지오 장지예 · 짱샘의 책방
          </div>
        </div>

        {/* Google OAuth */}
        <button
          onClick={signInGoogle}
          disabled={loading}
          style={{
            width:'100%',padding:'13px 16px',borderRadius:9999,
            border:'1px solid #dadce0',background:'#fff',color:'#3c4043',
            fontSize:14,fontWeight:700,fontFamily:'inherit',cursor:'pointer',
            display:'flex',alignItems:'center',justifyContent:'center',gap:10,
            marginBottom:14,opacity:loading?0.6:1,
            boxShadow:'0 1px 2px rgba(0,0,0,0.04)',
          }}
        >
          <GoogleLogo/>
          Google로 계속하기
        </button>

        {/* Kakao OAuth (Supabase 네이티브) */}
        <button
          onClick={signInKakao}
          disabled={loading}
          style={{
            width:'100%',padding:'13px 16px',borderRadius:9999,
            border:'none',background:'#FEE500',color:'#191600',
            fontSize:14,fontWeight:700,fontFamily:'inherit',cursor:'pointer',
            display:'flex',alignItems:'center',justifyContent:'center',gap:10,
            marginBottom:14,opacity:loading?0.6:1,
            boxShadow:'0 1px 2px rgba(0,0,0,0.04)',
          }}
        >
          <KakaoLogo/>
          카카오로 계속하기
        </button>

        {/* Naver 커스텀 OAuth (서버리스) */}
        {NAVER_LOGIN_ENABLED && (
          <button
            onClick={signInNaver}
            disabled={loading}
            style={{
              width:'100%',padding:'13px 16px',borderRadius:9999,
              border:'none',background:'#03C75A',color:'#fff',
              fontSize:14,fontWeight:700,fontFamily:'inherit',cursor:'pointer',
              display:'flex',alignItems:'center',justifyContent:'center',gap:10,
              marginBottom:14,opacity:loading?0.6:1,
              boxShadow:'0 1px 2px rgba(0,0,0,0.04)',
            }}
          >
            <NaverLogo/>
            네이버로 계속하기
          </button>
        )}

        <div className="hr-label" style={{margin:'2px 0 14px'}}>또는 이메일로</div>

        {/* Login / Signup tabs */}
        {mode !== 'forgot' && (
          <div style={{display:'flex',background:'#f3ece2',borderRadius:9999,padding:3,marginBottom:12}}>
            {[
              {k:'login', l:'로그인'},
              {k:'signup', l:'회원가입'},
            ].map(t=>(
              <button key={t.k} onClick={()=>{setMode(t.k); reset();}}
                style={{
                  flex:1,padding:'8px 0',borderRadius:9999,border:'none',
                  background: mode===t.k ? '#fff' : 'transparent',
                  color: mode===t.k ? '#2c5f8d' : '#718096',
                  fontSize:13,fontWeight:700,fontFamily:'inherit',cursor:'pointer',
                  boxShadow: mode===t.k ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                }}>
                {t.l}
              </button>
            ))}
          </div>
        )}

        {mode === 'forgot' && (
          <div style={{fontSize:13,fontWeight:700,color:'#2c5f8d',marginBottom:8}}>
            비밀번호 재설정
          </div>
        )}

        <input
          type="email" inputMode="email" autoComplete="email"
          value={email} onChange={(e)=>setEmail(e.target.value)}
          placeholder="이메일 주소"
          disabled={loading}
          style={{...INPUT_STYLE, marginBottom:8}}
        />

        {mode !== 'forgot' && (
          <input
            type="password"
            autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
            value={password} onChange={(e)=>setPassword(e.target.value)}
            onKeyDown={(e)=>{ if (e.key==='Enter' && mode!=='signup') submit(); }}
            placeholder={mode==='signup' ? '비밀번호 (6자 이상)' : '비밀번호'}
            disabled={loading}
            style={{...INPUT_STYLE, marginBottom: mode==='signup' ? 8 : 12}}
          />
        )}

        {mode === 'signup' && (
          <input
            type="password" autoComplete="new-password"
            value={confirmPw} onChange={(e)=>setConfirmPw(e.target.value)}
            onKeyDown={(e)=>{ if (e.key==='Enter') submit(); }}
            placeholder="비밀번호 확인"
            disabled={loading}
            style={{...INPUT_STYLE, marginBottom:12}}
          />
        )}

        <button
          onClick={submit} disabled={loading}
          className="btn btn-primary btn-block"
          style={{opacity:loading?0.6:1}}
        >
          {loading ? '처리 중...' :
            mode==='login' ? '로그인' :
            mode==='signup' ? '회원가입' :
            '재설정 링크 받기'}
        </button>

        {error && (
          <div style={{color:'#e74c3c',fontSize:12,marginTop:10,textAlign:'center',lineHeight:1.5}}>
            {error}
          </div>
        )}
        {info && (
          <div style={{color:'#1f7a3f',fontSize:12,marginTop:10,textAlign:'center',lineHeight:1.5,padding:'10px 12px',background:'#27ae6015',borderRadius:10}}>
            {info}
          </div>
        )}

        <div style={{marginTop:14,textAlign:'center'}}>
          {mode === 'login' && (
            <a onClick={()=>{setMode('forgot'); reset();}}
              style={{fontSize:12,color:'#2c5f8d',fontWeight:600,cursor:'pointer',textDecoration:'underline'}}>
              비밀번호를 잊으셨나요?
            </a>
          )}
          {mode === 'forgot' && (
            <a onClick={()=>{setMode('login'); reset();}}
              style={{fontSize:12,color:'#2c5f8d',fontWeight:600,cursor:'pointer',textDecoration:'underline'}}>
              ← 로그인으로 돌아가기
            </a>
          )}
        </div>

        <div style={{marginTop:18,fontSize:11,color:'#718096',textAlign:'center',lineHeight:1.6}}>
          계속하면 <u>이용약관</u> · <u>개인정보 처리방침</u>에 동의합니다.
        </div>
      </div>
    </div>
  );
}

// ————————————————————————————————————————————————————
// 0b. Password reset — shown when Supabase fires PASSWORD_RECOVERY
// ————————————————————————————————————————————————————
export function ScreenPasswordReset() {
  const [password, setPassword] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const submit = async () => {
    setError(null);
    if (password.length < 6) { setError("비밀번호는 6자 이상이어야 해요"); return; }
    if (password !== confirmPw) { setError("비밀번호 확인이 일치하지 않아요"); return; }
    setLoading(true);
    const { error: err } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (err) setError(err.message);
    else setDone(true);
  };

  const backToLogin = async () => {
    await supabase.auth.signOut();
    window.location.hash = "";
    window.location.reload();
  };

  if (done) {
    return (
      <div className="screen warm-gradient" style={{display:'flex',flexDirection:'column'}}>
        <div className="status-spacer"/>
        <div style={{flex:1,display:'flex',flexDirection:'column',justifyContent:'center',padding:'0 28px',textAlign:'center'}}>
          <div style={{fontSize:52,marginBottom:14}}>✅</div>
          <div style={{fontSize:22,fontWeight:800,letterSpacing:-0.3,marginBottom:10}}>
            비밀번호가 변경됐어요
          </div>
          <div style={{fontSize:13,color:'#4a5568',lineHeight:1.75,marginBottom:26}}>
            새 비밀번호로 다시 로그인해주세요.
          </div>
          <button className="btn btn-primary btn-block" onClick={backToLogin}>
            로그인 화면으로
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="screen warm-gradient" style={{display:'flex',flexDirection:'column'}}>
      <div className="status-spacer"/>
      <div style={{flex:1,display:'flex',flexDirection:'column',justifyContent:'center',padding:'0 28px'}}>
        <div style={{fontSize:38,marginBottom:10}}>🔐</div>
        <div style={{fontSize:24,fontWeight:800,letterSpacing:-0.5,lineHeight:1.25,marginBottom:8}}>
          새 비밀번호를<br/>설정해주세요
        </div>
        <div style={{fontSize:13,color:'#4a5568',lineHeight:1.7,marginBottom:18}}>
          6자 이상으로 입력해주세요.
        </div>

        <input
          type="password" autoComplete="new-password"
          value={password} onChange={(e)=>setPassword(e.target.value)}
          placeholder="새 비밀번호 (6자 이상)"
          disabled={loading}
          style={{...INPUT_STYLE, marginBottom:8}}
        />
        <input
          type="password" autoComplete="new-password"
          value={confirmPw} onChange={(e)=>setConfirmPw(e.target.value)}
          onKeyDown={(e)=>{ if (e.key==='Enter') submit(); }}
          placeholder="비밀번호 확인"
          disabled={loading}
          style={{...INPUT_STYLE, marginBottom:12}}
        />

        <button onClick={submit} disabled={loading} className="btn btn-primary btn-block"
          style={{opacity:loading?0.6:1}}>
          {loading ? '변경 중...' : '비밀번호 변경'}
        </button>

        {error && (
          <div style={{color:'#e74c3c',fontSize:12,marginTop:10,textAlign:'center',lineHeight:1.5}}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
}

// ————————————————————————————————————————————————————
// 0.5 Onboarding — first child registration (new users only)
// ————————————————————————————————————————————————————
export function ScreenOnboarding({ parentId, onDone }) {
  const [name, setName] = useState("");
  const [birthMonth, setBirthMonth] = useState("");
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const formatPhone = (v) => {
    const digits = v.replace(/\D/g, "").slice(0, 11);
    if (digits.length <= 3) return digits;
    if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
  };

  const save = async () => {
    const clean = name.trim();
    if (!clean) { setError("아이 이름을 입력해주세요"); return; }
    const phoneDigits = phone.replace(/\D/g, "");
    if (!phoneDigits || phoneDigits.length < 10) { setError("전화번호를 정확히 입력해주세요"); return; }
    setSaving(true);
    setError(null);
    const { error: phoneErr } = await supabase
      .from("it_users")
      .update({ phone: phoneDigits })
      .eq("id", parentId);
    if (phoneErr) { setSaving(false); setError(phoneErr.message); return; }
    const { data, error: err } = await supabase
      .from("it_children")
      .insert({
        parent_id: parentId,
        name: clean,
        birth_month: birthMonth ? `${birthMonth}-01` : null,
      })
      .select()
      .single();
    setSaving(false);
    if (err) { setError(err.message); return; }
    onDone(data);
  };

  const switchAccount = async () => {
    if (!window.confirm("로그아웃하고 다른 계정으로 로그인할까요?")) return;
    await supabase.auth.signOut();
  };

  return (
    <div className="screen warm-gradient" style={{display:'flex',flexDirection:'column'}}>
      <div className="status-spacer"/>
      <div style={{padding:'16px 28px 0',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
        <div style={{fontSize:12,color:'#718096',fontWeight:700}}>처음 시작하는 중</div>
        <a onClick={switchAccount}
          style={{fontSize:12,color:'#2c5f8d',fontWeight:600,cursor:'pointer',textDecoration:'underline'}}>
          다른 계정으로 로그인
        </a>
      </div>
      <div style={{flex:1,display:'flex',flexDirection:'column',justifyContent:'center',padding:'0 28px'}}>
        <div style={{fontSize:36,marginBottom:12}}>👶</div>
        <div style={{fontSize:24,fontWeight:800,letterSpacing:-0.3,lineHeight:1.3,marginBottom:10}}>
          우리 아이를<br/>소개해주세요
        </div>
        <div style={{fontSize:13,color:'#4a5568',lineHeight:1.75,marginBottom:24}}>
          이름과 태어난 시기만 있으면 시작할 수 있어요.<br/>
          더 많은 아이는 나중에 추가하실 수 있어요.
        </div>

        <div style={{marginBottom:14}}>
          <div style={{fontSize:12,fontWeight:700,color:'#2c5f8d',marginBottom:6}}>아이 이름</div>
          <input
            type="text"
            value={name}
            onChange={(e)=>setName(e.target.value)}
            placeholder="예: 김민준"
            disabled={saving}
            style={{width:'100%',padding:'14px 16px',borderRadius:12,border:'1px solid #e2e8f0',
              fontSize:15,fontFamily:'inherit',background:'#fff'}}
          />
        </div>

        <div style={{marginBottom:14}}>
          <div style={{fontSize:12,fontWeight:700,color:'#2c5f8d',marginBottom:6}}>보호자 연락처</div>
          <input
            type="tel"
            inputMode="numeric"
            value={phone}
            onChange={(e)=>setPhone(formatPhone(e.target.value))}
            placeholder="010-0000-0000"
            disabled={saving}
            style={{width:'100%',padding:'14px 16px',borderRadius:12,border:'1px solid #e2e8f0',
              fontSize:15,fontFamily:'inherit',background:'#fff'}}
          />
          <div style={{fontSize:11,color:'#718096',marginTop:4}}>리포트 도착 알림을 보내드려요.</div>
        </div>

        <div style={{marginBottom:24}}>
          <div style={{fontSize:12,fontWeight:700,color:'#2c5f8d',marginBottom:6}}>태어난 년월 (선택)</div>
          <input
            type="month"
            value={birthMonth}
            onChange={(e)=>setBirthMonth(e.target.value)}
            disabled={saving}
            style={{width:'100%',padding:'14px 16px',borderRadius:12,border:'1px solid #e2e8f0',
              fontSize:15,fontFamily:'inherit',background:'#fff'}}
          />
        </div>

        <button className="btn btn-primary btn-block" onClick={save}
          disabled={saving || !name.trim() || phone.replace(/\D/g,"").length < 10}
          style={{opacity:(saving||!name.trim())?0.6:1}}>
          {saving ? "저장 중..." : "시작하기"}
        </button>

        {error && (
          <div style={{color:'#e74c3c',fontSize:12,marginTop:10,textAlign:'center'}}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
}

// ————————————————————————————————————————————————————
// 1. Home — real data via props
// ————————————————————————————————————————————————————
export function ScreenHome({
  profile,
  child,
  activeCase,
  pastReports,
  creditBalance = 0,
  onNewCase,
  onOpenCase,
  onOpenReport,
  onBuyPackage,
}) {
  const parentName =
    profile?.display_name || (child?.name ? `${child.name} 엄마` : "부모님");
  const childAge = formatAge(child?.birth_month);
  const caseStatus = activeCase?.status;

  const concernSummary = activeCase?.concern_tags?.length
    ? `${activeCase.concern_tags[0]} · 영상 ${activeCase.video_count ?? 0}개 제출`
    : "분석 대기 중";

  return (
    <div className="screen" style={{paddingBottom:90}}>
      <div className="status-spacer"/>
      <div className="app-header" style={{display:'flex',alignItems:'center',justifyContent:'space-between',paddingRight:14}}>
        <div className="app-title"><span className="mark">📖</span>i-talk</div>
        <div onClick={onBuyPackage} style={{
          cursor:'pointer',display:'flex',alignItems:'center',gap:6,
          background: creditBalance > 0 ? '#2c5f8d10' : '#fef5e7',
          color: creditBalance > 0 ? '#2c5f8d' : '#b7791f',
          border: '1px solid '+(creditBalance > 0 ? '#2c5f8d33' : '#f6c97e'),
          borderRadius:9999,padding:'6px 12px',fontSize:11,fontWeight:800,letterSpacing:.2}}>
          <span style={{fontSize:13}}>🎟</span>
          잔여 분석권 {creditBalance}회
          {creditBalance === 0 && <span style={{color:'#e6852b',fontSize:10}}>· 구매</span>}
        </div>
      </div>

      <div style={{padding:'18px 20px 0'}}>
        <div style={{fontSize:13,color:'#718096'}}>안녕하세요, {parentName}님 👋</div>
        <div style={{fontSize:20,fontWeight:800,letterSpacing:-0.3,marginTop:2,color:'#1a1a2e'}}>
          오늘 아이 상태는 어떠세요?
        </div>
      </div>

      {activeCase && (
        <div style={{padding:'18px 20px 0'}}>
          <div className="sec-lbl">진행 중인 분석</div>
          <div className="card" style={{cursor:'pointer'}} onClick={onOpenCase}>
            <div style={{display:'flex',gap:12,alignItems:'center'}}>
              <div className="char-round" style={{display:'flex',alignItems:'center',justifyContent:'center',fontSize:22}}>
                {child?.avatar_url
                  ? <img src={child.avatar_url} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>
                  : '👶'}
              </div>
              <div style={{flex:1}}>
                <div style={{fontWeight:800,fontSize:15}}>
                  {child?.name}
                  {childAge && <span style={{fontWeight:500,color:'#718096',fontSize:12}}> · {childAge}</span>}
                </div>
                <div style={{fontSize:12,color:'#4a5568',marginTop:2}}>{concernSummary}</div>
              </div>
              {caseStatus === 'analyzing' && <span className="status-chip status-2"><span className="spinner" style={{width:10,height:10,borderWidth:2}}/>분석 중</span>}
              {(caseStatus === 'research' || caseStatus === 'reviewing') && <span className="status-chip status-2"><span className="spinner" style={{width:10,height:10,borderWidth:2}}/>리서치 중</span>}
              {caseStatus === 'done' && <span className="status-chip status-3">✓ 완료</span>}
              {caseStatus === 'received' && <span className="status-chip status-1">📥 접수됨</span>}
            </div>
            <div style={{marginTop:14,height:4,borderRadius:2,background:'#f3ece2',overflow:'hidden'}}>
              <div style={{
                width:
                  caseStatus === 'received' ? '33%' :
                  (caseStatus === 'analyzing' || caseStatus === 'research' || caseStatus === 'reviewing') ? '66%' :
                  caseStatus === 'done' ? '100%' : '10%',
                height:'100%',background:'linear-gradient(90deg,#2c5f8d,#ff9933)', transition:'width .5s'
              }}/>
            </div>
            <div style={{marginTop:6,display:'flex',justifyContent:'space-between',fontSize:10,color:'#718096',fontWeight:600}}>
              <span style={{color:'#2c5f8d'}}>1. 접수</span>
              <span style={{color: (caseStatus === 'analyzing' || caseStatus === 'research' || caseStatus === 'reviewing' || caseStatus === 'done') ? '#e6852b' : '#718096'}}>2. 분석</span>
              <span style={{color: caseStatus === 'done' ? '#1f7a3f' : '#718096'}}>3. 완료</span>
            </div>
          </div>
        </div>
      )}

      {!activeCase && child && (
        <div style={{padding:'18px 20px 0'}}>
          <div className="card" style={{display:'flex',alignItems:'center',gap:12}}>
            <div className="char-round" style={{display:'flex',alignItems:'center',justifyContent:'center',fontSize:22}}>
              {child.avatar_url
                ? <img src={child.avatar_url} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>
                : '👶'}
            </div>
            <div style={{flex:1}}>
              <div style={{fontWeight:800,fontSize:15}}>
                {child.name}
                {childAge && <span style={{fontWeight:500,color:'#718096',fontSize:12}}> · {childAge}</span>}
              </div>
              <div style={{fontSize:12,color:'#718096',marginTop:2}}>분석 이력이 아직 없어요</div>
            </div>
          </div>
        </div>
      )}

      <div style={{padding:'22px 20px 0'}}>
        <div onClick={onNewCase} style={{
          cursor:'pointer',
          background:'linear-gradient(135deg, #fffbf5, #fef9f0)',
          border:'1.5px dashed #e6852b',
          borderRadius:20, padding:'18px 18px',
          display:'flex',alignItems:'center',gap:14}}>
          <div style={{width:44,height:44,borderRadius:12,background:'#ff9933',color:'#fff',
            display:'flex',alignItems:'center',justifyContent:'center',fontSize:20}}>🎬</div>
          <div style={{flex:1}}>
            <div style={{fontWeight:800,fontSize:14,color:'#1a1a2e'}}>새 영상 분석 요청하기</div>
            <div style={{fontSize:11,color:'#718096',marginTop:2}}>1분 미만 영상 5~6개면 충분해요</div>
          </div>
          <div style={{fontSize:20,color:'#e6852b'}}>→</div>
        </div>
      </div>

      <div style={{padding:'22px 20px 0'}}>
        <div className="sec-lbl">지난 리포트</div>
        {pastReports && pastReports.length > 0 ? (
          <div style={{display:'flex',flexDirection:'column',gap:10}}>
            {pastReports.map((r)=>(
              <div key={r.case_id} className="card-flat"
                onClick={() => onOpenReport?.(r.case_id)}
                style={{display:'flex',alignItems:'center',gap:12,cursor:'pointer'}}>
                <div style={{width:36,height:36,borderRadius:8,background:'#2c5f8d14',
                  display:'flex',alignItems:'center',justifyContent:'center',fontSize:16}}>💌</div>
                <div style={{flex:1}}>
                  <div style={{fontWeight:700,fontSize:13}}>{r.title || '분석 리포트'}</div>
                  <div style={{fontSize:11,color:'#718096',marginTop:1}}>{formatDate(r.finalized_at)}</div>
                </div>
                <span className="pill pill-soft">{r.viewed_at ? '열람함' : '새 리포트'}</span>
              </div>
            ))}
          </div>
        ) : (
          <div style={{padding:'22px 16px',background:'#fff',border:'1px dashed #e2e8f0',borderRadius:16,textAlign:'center'}}>
            <div style={{fontSize:24,marginBottom:6}}>💌</div>
            <div style={{fontSize:12,color:'#718096',lineHeight:1.7}}>
              아직 받으신 리포트가 없어요.<br/>
              위 버튼으로 첫 분석을 시작해보세요.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function TabBar({ active, onChange, unreadCount = 0 }) {
  const tabs = [
    {k:'home', em:'🏠', lbl:'홈'},
    {k:'notifications', em:'🔔', lbl:'알림'},
    {k:'settings', em:'⚙️', lbl:'설정'},
  ];
  return (
    <div className="tab-bar">
      {tabs.map(t=>(
        <div key={t.k}
          onClick={() => onChange?.(t.k)}
          className={'tab'+(active===t.k?' active':'')}
          style={{cursor:'pointer',position:'relative'}}>
          <span className="em">{t.em}</span>
          {t.k === 'notifications' && unreadCount > 0 && (
            <div style={{
              position:'absolute', top:0, right:'calc(50% - 22px)',
              minWidth:16, height:16, padding:'0 4px',
              borderRadius:8, background:'#e74c3c', color:'#fff',
              fontSize:9, fontWeight:800,
              display:'flex', alignItems:'center', justifyContent:'center',
              border:'2px solid #fff', boxSizing:'content-box',
            }}>
              {unreadCount > 9 ? '9+' : unreadCount}
            </div>
          )}
          {t.lbl}
        </div>
      ))}
    </div>
  );
}

// ————————————————————————————————————————————————————
// 2. Concern form
// ————————————————————————————————————————————————————
export function ScreenConcern({ caseRecord, onCaseUpdate, onBack, onNext }) {
  const presets = ['호흡', '감각', '후각', '원시반사', '수면', '식습관', '자세', '대근육', '소근육', '언어', '각성'];
  const [picked, setPicked] = useState(
    caseRecord?.concern_tags?.length ? caseRecord.concern_tags : ['원시반사']
  );
  const [memo, setMemo] = useState(caseRecord?.concern_memo ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const toggle = (p) => setPicked((prev) =>
    prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]
  );

  const handleNext = async () => {
    if (!caseRecord?.id) return;
    setSaving(true);
    setError(null);
    try {
      await updateCaseConcern({
        caseId: caseRecord.id,
        tags: picked,
        memo: memo.trim() || null,
      });
      onCaseUpdate?.({ ...caseRecord, concern_tags: picked, concern_memo: memo });
      onNext();
    } catch (err) {
      setError(err.message ?? '저장에 실패했어요');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="screen" style={{paddingBottom:100}}>
      <div className="status-spacer"/>
      <div style={{display:'flex',alignItems:'center',padding:'8px 8px 14px',gap:4}}>
        <button onClick={onBack}
          style={{display:'flex',alignItems:'center',gap:4,border:'none',background:'transparent',padding:'10px 12px',cursor:'pointer'}}>
          <span style={{fontSize:18}}>←</span>
          <span style={{fontSize:12,color:'#718096',fontWeight:700}}>1 / 3 · 아이 관찰</span>
        </button>
      </div>

      <div style={{padding:'0 22px'}}>
        <div style={{fontSize:22,fontWeight:800,letterSpacing:-0.3,lineHeight:1.3,marginBottom:8}}>
          어떤 점이<br/>가장 마음에 걸리셨나요?
        </div>
        <div style={{fontSize:13,color:'#4a5568',lineHeight:1.7,marginBottom:22}}>
          간단히 말씀만 해주셔도 충분해요.<br/>
          자세한 건 영상을 보고 짱샘이 정리해드려요.
        </div>

        <div style={{marginBottom:18}}>
          <div style={{fontSize:12,fontWeight:700,color:'#2c5f8d',marginBottom:8}}>관련 있는 항목</div>
          <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
            {presets.map(p=>(
              <div key={p} onClick={()=>toggle(p)}
                style={{padding:'8px 14px',borderRadius:9999,fontSize:12,fontWeight:600,cursor:'pointer',
                background: picked.includes(p)?'#2c5f8d':'#fff',
                color: picked.includes(p)?'#fff':'#4a5568',
                border:'1px solid '+(picked.includes(p)?'#2c5f8d':'#e2e8f0')}}>
                {picked.includes(p) && '✓ '}{p}
              </div>
            ))}
          </div>
        </div>

        <div>
          <div style={{fontSize:12,fontWeight:700,color:'#2c5f8d',marginBottom:8}}>자유 메모 (선택)</div>
          <textarea value={memo} onChange={e=>setMemo(e.target.value.slice(0,500))}
            placeholder="예: 갑작스러운 소리가 나면 팔을 벌렸다 다시 껴안듯 오므려요. 요즘 들어 잠잘 때도 자주 보여요."
            style={{width:'100%',minHeight:120,padding:14,borderRadius:12,border:'1px solid #e2e8f0',
            fontSize:13,fontFamily:'inherit',resize:'none',lineHeight:1.6,background:'#fffdf8'}}/>
          <div style={{marginTop:6,fontSize:11,color:'#718096',textAlign:'right'}}>{memo.length} / 500</div>
        </div>

        {error && (
          <div style={{color:'#e74c3c',fontSize:12,marginTop:12,textAlign:'center'}}>{error}</div>
        )}
      </div>

      <div style={{position:'absolute',bottom:0,left:0,right:0,padding:'16px 20px 34px',
        background:'linear-gradient(180deg,transparent,#fffbf5 30%)'}}>
        <button className="btn btn-primary btn-block"
          onClick={handleNext} disabled={saving || picked.length===0}
          style={{opacity:(saving||picked.length===0)?0.6:1}}>
          {saving ? '저장 중...' : '다음: 영상 올리기'}
        </button>
      </div>
    </div>
  );
}

// ————————————————————————————————————————————————————
// 3. Upload — up to 6 videos, each ≤ 1 minute
// ————————————————————————————————————————————————————
function formatDuration(seconds) {
  if (seconds == null) return '';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function ScreenUpload({ caseRecord, userId, onBack, onNext, submitting = false, creditBalance = 0 }) {
  const tipGroups = [
    {
      t: "정적 자세",
      s: "특별한 자극 없이 자연스럽게 있는 전면·측면·후면 모습",
      items: [
        { em:'🛌', t:'바로 누운 자세' },
        { em:'🤸', t:'엎드린 자세' },
        { em:'🪑', t:'앉은 자세' },
        { em:'🧍', t:'서 있는 모습' },
      ],
    },
    {
      t: "동적 자세",
      s: "평소 노는 모습 혹은 개선이 필요한 부분",
      items: [
        { em:'🚶', t:'걷는 모습' },
        { em:'🧸', t:'노는 모습' },
        { em:'🍽️', t:'식사 모습' },
        { em:'🗣️', t:'말하는 모습' },
      ],
    },
  ];

  // slots[i] = null | { id, position, duration_seconds, storage_path, thumbnail_path }
  const [slots, setSlots] = useState(Array(6).fill(null));
  const [loading, setLoading] = useState(true);
  const [busyIndex, setBusyIndex] = useState(null);
  const [error, setError] = useState(null);
  // storage_path → signed URL for the .thumb.jpg, used when localStorage has
  // no entry (cross-device, or local capture failed).
  const [remoteThumbs, setRemoteThumbs] = useState({});

  const load = async () => {
    try {
      const videos = await getCaseVideos(caseRecord.id);
      const next = Array(6).fill(null);
      videos.forEach((v) => {
        if (v.position >= 1 && v.position <= 6) next[v.position - 1] = v;
      });
      setSlots(next);
    } catch (err) {
      setError(err.message ?? '영상 목록을 불러오지 못했어요');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [caseRecord?.id]);

  // Fetch signed URLs for slots that have a remote thumbnail but no
  // localStorage entry (cross-device, or local capture failed).
  useEffect(() => {
    const targets = slots.filter(
      (v) => v && v.thumbnail_path && !getThumb(v.storage_path) && !remoteThumbs[v.storage_path]
    );
    if (!targets.length) return;
    let cancelled = false;
    (async () => {
      const next = {};
      await Promise.all(targets.map(async (v) => {
        const url = await getRemoteThumbUrl(v.thumbnail_path);
        if (url) next[v.storage_path] = url;
      }));
      if (cancelled || !Object.keys(next).length) return;
      setRemoteThumbs((prev) => ({ ...prev, ...next }));
    })();
    return () => { cancelled = true; };
  }, [slots, remoteThumbs]);

  const pick = (index) => {
    if (busyIndex !== null) return;
    if (slots[index]) return;

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'video/*';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setBusyIndex(index);
      setError(null);
      try {
        const video = await uploadCaseVideo({
          caseId: caseRecord.id,
          userId,
          position: index + 1,
          file,
        });
        setSlots((prev) => {
          const n = [...prev];
          n[index] = video;
          return n;
        });
      } catch (err) {
        setError(err.message ?? '업로드에 실패했어요');
      } finally {
        setBusyIndex(null);
      }
    };
    input.click();
  };

  const remove = async (index) => {
    const v = slots[index];
    if (!v || busyIndex !== null) return;
    if (!window.confirm('이 영상을 삭제할까요?')) return;
    setBusyIndex(index);
    setError(null);
    try {
      await deleteCaseVideo({ videoId: v.id, storagePath: v.storage_path });
      setSlots((prev) => {
        const n = [...prev];
        n[index] = null;
        return n;
      });
    } catch (err) {
      setError(err.message ?? '삭제에 실패했어요');
    } finally {
      setBusyIndex(null);
    }
  };

  const doneCount = slots.filter(Boolean).length;
  const colors = ['#2c5f8d','#e17055','#78c1c8','#e8a87c','#9b89b3','#5b7b9a'];
  const colorsEnd = ['#3a7ab5','#e8a87c','#00b894','#ff9933','#6c5ce7','#2c5f8d'];

  return (
    <div className="screen" style={{paddingBottom:140}}>
      <div className="status-spacer"/>
      <div style={{display:'flex',alignItems:'center',padding:'8px 8px 14px',gap:4}}>
        <button onClick={onBack}
          style={{display:'flex',alignItems:'center',gap:4,border:'none',background:'transparent',padding:'10px 12px',cursor:'pointer'}}>
          <span style={{fontSize:18}}>←</span>
          <span style={{fontSize:12,color:'#718096',fontWeight:700}}>2 / 2 · 영상 업로드</span>
        </button>
        <div style={{marginLeft:'auto',fontSize:11,color:'#2c5f8d',fontWeight:700,padding:'4px 10px',background:'#2c5f8d10',borderRadius:9999}}>
          🎟 {creditBalance}회 남음
        </div>
      </div>

      <div style={{padding:'0 22px 0'}}>
        <div style={{fontSize:22,fontWeight:800,letterSpacing:-0.3,lineHeight:1.3,marginBottom:8}}>
          1분 미만 영상을<br/>5~6개 올려주세요
        </div>
        <div style={{fontSize:13,color:'#4a5568',lineHeight:1.7,marginBottom:18}}>
          평소 모습이면 충분해요. 너무 잘 찍으려 하지 마세요.
        </div>

        <div style={{background:'#2c5f8d08',borderRadius:14,padding:'12px 14px',marginBottom:20,
          border:'1px solid #2c5f8d14'}}>
          <div style={{fontSize:11,fontWeight:800,color:'#2c5f8d',marginBottom:10,letterSpacing:.3}}>
            👩‍⚕️ 짱샘의 촬영 팁
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:12}}>
            {tipGroups.map((g,gi)=>(
              <div key={gi}>
                <div style={{fontSize:12,fontWeight:800,color:'#1a1a2e',marginBottom:2}}>{g.t}</div>
                <div style={{fontSize:10.5,color:'#718096',marginBottom:6,lineHeight:1.5}}>{g.s}</div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6}}>
                  {g.items.map((it,i)=>(
                    <div key={i} style={{display:'flex',gap:6,alignItems:'center'}}>
                      <span style={{fontSize:13}}>{it.em}</span>
                      <div style={{fontSize:11.5,fontWeight:600,color:'#2d3748'}}>{it.t}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10}}>
          {slots.map((v,i)=>{
            const isBusy = busyIndex === i;
            const done = !!v;
            const thumb = done ? (getThumb(v.storage_path) || remoteThumbs[v.storage_path] || null) : null;
            return (
              <div key={i}
                className={'vslot'+(done?' done':'')}
                onClick={() => done ? remove(i) : pick(i)}
                style={{cursor: busyIndex!==null && !isBusy ? 'not-allowed' : 'pointer',
                  opacity: busyIndex!==null && !isBusy ? 0.5 : 1}}>
                {done && !isBusy && (
                  <Fragment>
                    {thumb ? (
                      <img src={thumb} alt=""
                        style={{position:'absolute',inset:0,width:'100%',height:'100%',objectFit:'cover'}}/>
                    ) : (
                      <div style={{position:'absolute',inset:0,background:
                        `linear-gradient(135deg, ${colors[i%6]}, ${colorsEnd[i%6]})`}}/>
                    )}
                    <div style={{position:'absolute',inset:0,background:'rgba(0,0,0,.18)'}}/>
                    <div style={{position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center',
                      fontSize:22,color:'#fff',textShadow:'0 1px 4px rgba(0,0,0,.5)'}}>▶</div>
                    <div className="badge">#{i+1}</div>
                    <div className="dur">{formatDuration(v.duration_seconds)}</div>
                  </Fragment>
                )}
                {isBusy && (
                  <div className="ph" style={{color:'#2c5f8d'}}>
                    <div className="spinner"/>
                    <span style={{marginTop:4}}>{done ? '삭제 중...' : '업로드 중...'}</span>
                  </div>
                )}
                {!done && !isBusy && (
                  <div className="ph">
                    <span className="big">+</span>
                    <span>영상 #{i+1}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div style={{marginTop:14,fontSize:12,color:'#718096',textAlign:'center'}}>
          {loading ? '불러오는 중...' : `${doneCount} / 6 업로드됨 · 최대 1분 · 각 영상을 탭하면 삭제돼요`}
        </div>

        {error && (
          <div style={{color:'#e74c3c',fontSize:12,marginTop:10,textAlign:'center',lineHeight:1.5}}>{error}</div>
        )}
      </div>

      <div style={{position:'absolute',bottom:0,left:0,right:0,padding:'16px 20px 34px',
        background:'linear-gradient(180deg,transparent,#fffbf5 30%)'}}>
        <button className="btn btn-primary btn-block"
          disabled={doneCount<3 || busyIndex!==null || submitting}
          onClick={onNext}
          style={{opacity:(doneCount<3||busyIndex!==null||submitting)?0.4:1}}>
          {submitting ? '제출 중...' :
            doneCount<3 ? '최소 3개는 올려주세요' :
            '제출하기 (분석권 1회 사용)'}
        </button>
        <div style={{marginTop:8,fontSize:11,color:'#718096',textAlign:'center',lineHeight:1.6}}>
          제출하면 잔여 분석권에서 1회가 차감돼요
        </div>
      </div>
    </div>
  );
}

// ————————————————————————————————————————————————————
// 4. Packages — 패키지형 1회성 결제 (1/3/5/10회). 정기결제 X.
//    잔여 분석권은 it_credit_packages 테이블, 케이스 만들 때 1씩 차감.
// ————————————————————————————————————————————————————
export function ScreenPackages({ profile, session, creditBalance = 0, onBack }) {
  const [plan, setPlan] = useState("pkg4");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const userId = session?.user?.id;
  const email = session?.user?.email ?? null;
  const displayName =
    profile?.display_name || (email ? email.split("@")[0] : null);
  const selected = PACKAGE_OPTIONS.find((p) => p.key === plan) ?? PACKAGE_OPTIONS[1];

  const handleSubmit = async () => {
    if (!userId) { setError("로그인이 필요해요"); return; }
    setSubmitting(true);
    setError(null);
    try {
      const intent = await createPaymentIntent({ planType: plan });
      const toss = await getTossPayments();
      const payment = toss.payment({ customerKey: userId });
      const origin = window.location.origin;
      await payment.requestPayment({
        method: "CARD",
        amount: { currency: "KRW", value: intent.amount },
        orderId: intent.orderId,
        orderName: intent.orderName,
        successUrl: `${origin}/pay/success?kind=package`,
        failUrl: `${origin}/pay/fail?kind=package`,
        customerEmail: email ?? undefined,
        customerName: displayName ?? undefined,
        card: { useEscrow: false, flowMode: "DEFAULT", useCardPoint: false, useAppCardOnly: false },
      });
    } catch (err) {
      setError(err?.message ?? "결제를 시작할 수 없어요");
      setSubmitting(false);
    }
  };

  return (
    <div className="screen" style={{paddingBottom:140}}>
      <div className="status-spacer"/>
      <div style={{display:'flex',alignItems:'center',padding:'8px 8px 14px',gap:4}}>
        <button onClick={onBack}
          style={{display:'flex',alignItems:'center',gap:4,border:'none',background:'transparent',padding:'10px 12px',cursor:'pointer'}}>
          <span style={{fontSize:18}}>←</span>
          <span style={{fontSize:12,color:'#718096',fontWeight:700}}>분석권 패키지</span>
        </button>
      </div>

      <div style={{padding:'0 22px'}}>
        <div style={{fontSize:22,fontWeight:800,letterSpacing:-0.3,lineHeight:1.3,marginBottom:8}}>
          분석권을 충전해주세요
        </div>
        <div style={{fontSize:13,color:'#4a5568',lineHeight:1.7,marginBottom:14}}>
          한 번 결제로 여러 케이스를 분석할 수 있어요.<br/>
          많이 살수록 회당 단가가 저렴해져요.
        </div>

        <div style={{
          background:'#2c5f8d08',border:'1px solid #2c5f8d22',borderRadius:12,
          padding:'10px 14px',marginBottom:18,fontSize:12,color:'#2c5f8d',fontWeight:700}}>
          🎟 현재 잔여 분석권 <b style={{fontSize:14}}>{creditBalance}회</b>
        </div>

        <div style={{display:'flex',flexDirection:'column',gap:12}}>
          {PACKAGE_OPTIONS.map((opt) => {
            const active = plan === opt.key;
            return (
              <div key={opt.key} onClick={() => setPlan(opt.key)} style={{
                cursor:'pointer', background: active ? '#fffbf5' : '#fff',
                border: '2px solid '+(active ? '#ff9933' : '#e2e8f0'),
                borderRadius:18, padding:'18px 18px', position:'relative'}}>
                {opt.badge && (
                  <div style={{position:'absolute',top:-10,right:16,background:'#e6852b',color:'#fff',
                    fontSize:10,fontWeight:800,padding:'4px 10px',borderRadius:9999}}>{opt.badge}</div>
                )}
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:8}}>
                  <div>
                    <div style={{fontSize:11,fontWeight:800,color:active?'#e6852b':'#2c5f8d',marginBottom:3,letterSpacing:.5,textTransform:'uppercase'}}>
                      {opt.credits}회 패키지
                    </div>
                    <div style={{fontSize:17,fontWeight:800}}>분석권 {opt.credits}회</div>
                  </div>
                  <div style={{
                    width:22,height:22,borderRadius:11,
                    border:'2px solid '+(active?'#ff9933':'#cbd5e0'),
                    background:active?'#ff9933':'#fff',
                    display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:11}}>
                    {active && '✓'}
                  </div>
                </div>
                {opt.discountPct && opt.original && (
                  <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
                    <span style={{
                      fontSize:11,fontWeight:800,color:'#fff',background:'#e74c3c',
                      padding:'3px 9px',borderRadius:9999,letterSpacing:.3}}>
                      {opt.discountPct}% 할인
                    </span>
                    <span style={{fontSize:14,color:'#a0aec0',fontWeight:600,textDecoration:'line-through'}}>
                      {opt.original.toLocaleString('ko-KR')}원
                    </span>
                  </div>
                )}
                <div style={{display:'flex',alignItems:'baseline',gap:8,marginBottom:6}}>
                  <span style={{fontSize:24,fontWeight:800,color:'#e74c3c'}}>
                    {opt.total.toLocaleString('ko-KR')}원
                  </span>
                </div>
                <div style={{fontSize:12,color:'#4a5568',lineHeight:1.6}}>
                  회당 <b>{opt.perCase.toLocaleString('ko-KR')}원</b>
                  {opt.key !== 'pkg1' && (
                    <span style={{color:'#1f7a3f',marginLeft:6}}>
                      · 1회권 대비 {(((PACKAGE_OPTIONS[0].perCase - opt.perCase) / PACKAGE_OPTIONS[0].perCase) * 100).toFixed(0)}% 할인
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{
          marginTop:18,padding:'12px 14px',borderRadius:12,
          background:'#fff',border:'1px dashed #cbd5e0',
          fontSize:11,color:'#718096',lineHeight:1.7}}>
          ✓ 결제 즉시 잔여 분석권에 충전돼요<br/>
          ✓ 만료일 없음 · 케이스 만들 때마다 1회 차감<br/>
          ✓ 환불은 미사용분 비례로 안내드려요
        </div>
      </div>

      <div style={{position:'absolute',bottom:0,left:0,right:0,padding:'16px 20px 34px',
        background:'linear-gradient(180deg,transparent,#fffbf5 40%)'}}>
        <button className="btn btn-secondary btn-block"
          onClick={handleSubmit}
          disabled={submitting}
          style={{opacity:submitting?0.55:1}}>
          {submitting
            ? '결제창 여는 중...'
            : `${selected.total.toLocaleString('ko-KR')}원 결제 (${selected.credits}회 충전)`}
        </button>
        {error && (
          <div style={{color:'#e74c3c',fontSize:12,marginTop:10,textAlign:'center',lineHeight:1.5}}>{error}</div>
        )}
      </div>
    </div>
  );
}

// ————————————————————————————————————————————————————
// 4-legacy. Single-case Payment — kept for backward compatibility but
//   not reachable from the package flow. ParentApp calls
//   consumeCreditAndSubmit() instead of opening this screen.
// ————————————————————————————————————————————————————
const PAY_METHODS = [
  { k: "CARD", lbl: "카드", emoji: "💳" },
  { k: "TRANSFER", lbl: "계좌이체", emoji: "🏦" },
];

export function ScreenPay({ caseRecord, profile, session, onBack, plan, setPlan }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [payMethod, setPayMethod] = useState("CARD");

  const userId = session?.user?.id;
  const email = session?.user?.email ?? null;
  const displayName =
    profile?.display_name || (email ? email.split("@")[0] : null);

  const isSubscription = plan === "basic" || plan === "pro";
  const amount = PLAN_PRICE[plan] ?? PLAN_PRICE.once;

  const handleSubmit = async () => {
    if (!caseRecord?.id) return;
    if (isSubscription) {
      setError("구독 결제는 곧 준비됩니다. 지금은 1회 분석만 가능해요.");
      return;
    }
    if (!userId) {
      setError("로그인이 필요해요");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      // 1) Server creates pending purchase and returns orderId/amount
      const intent = await createPaymentIntent({
        caseId: caseRecord.id,
        planType: "once",
      });

      // 2) Open Toss payment modal with the selected method
      const toss = await getTossPayments();
      const payment = toss.payment({ customerKey: userId });

      const origin = window.location.origin;
      const common = {
        amount: { currency: "KRW", value: intent.amount },
        orderId: intent.orderId,
        orderName: intent.orderName,
        successUrl: `${origin}/pay/success?case_id=${caseRecord.id}`,
        failUrl: `${origin}/pay/fail?case_id=${caseRecord.id}`,
        customerEmail: email ?? undefined,
        customerName: displayName ?? undefined,
      };

      if (payMethod === "TRANSFER") {
        await payment.requestPayment({
          ...common,
          method: "TRANSFER",
          transfer: { cashReceipt: { type: "소득공제" } },
        });
      } else {
        await payment.requestPayment({
          ...common,
          method: "CARD",
          card: {
            useEscrow: false,
            flowMode: "DEFAULT",
            useCardPoint: false,
            useAppCardOnly: false,
          },
        });
      }
      // Toss redirects away; if this line runs, user likely canceled.
    } catch (err) {
      // Toss SDK throws with { code, message } on user cancel etc.
      setError(err?.message ?? "결제를 시작할 수 없어요");
      setSubmitting(false);
    }
  };

  return (
    <div className="screen" style={{paddingBottom:120}}>
      <div className="status-spacer"/>
      <div style={{display:'flex',alignItems:'center',padding:'8px 8px 14px',gap:4}}>
        <button onClick={onBack}
          style={{display:'flex',alignItems:'center',gap:4,border:'none',background:'transparent',padding:'10px 12px',cursor:'pointer'}}>
          <span style={{fontSize:18}}>←</span>
          <span style={{fontSize:12,color:'#718096',fontWeight:700}}>3 / 3 · 결제</span>
        </button>
      </div>

      <div style={{padding:'0 22px'}}>
        <div style={{fontSize:22,fontWeight:800,letterSpacing:-0.3,lineHeight:1.3,marginBottom:8}}>
          요금제를 골라주세요
        </div>
        <div style={{fontSize:13,color:'#4a5568',lineHeight:1.7,marginBottom:22}}>
          1회 분석, 또는 월 구독으로 받아볼 수 있어요.
        </div>

        <div style={{display:'flex',flexDirection:'column',gap:12}}>
          <div onClick={()=>setPlan('once')} style={{
            cursor:'pointer', background:'#fff',
            border:'2px solid '+(plan==='once'?'#2c5f8d':'#e2e8f0'),
            borderRadius:18, padding:'18px 18px', position:'relative'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:8}}>
              <div>
                <div style={{fontSize:11,fontWeight:800,color:'#2c5f8d',marginBottom:3,letterSpacing:.5,textTransform:'uppercase'}}>1회 분석</div>
                <div style={{fontSize:17,fontWeight:800}}>이번 한 번만 받아볼게요</div>
              </div>
              <div style={{width:22,height:22,borderRadius:11,border:'2px solid '+(plan==='once'?'#2c5f8d':'#cbd5e0'),
                background:plan==='once'?'#2c5f8d':'#fff',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:11}}>
                {plan==='once' && '✓'}
              </div>
            </div>
            <div style={{display:'flex',alignItems:'baseline',gap:8,marginBottom:10}}>
              <span style={{fontSize:24,fontWeight:800,color:'#1a1a2e'}}>49,000원</span>
              <span className="pill pill-soft" style={{fontSize:10}}>1건</span>
            </div>
            <div style={{fontSize:12,color:'#4a5568',lineHeight:1.7}}>
              ✓ 영상 6개 분석 · 운영진 검토<br/>
              ✓ Haiku 4.5 기반 근거 자료 리서치
            </div>
          </div>

          <div onClick={()=>setPlan('basic')} style={{
            cursor:'pointer', background:'#fff',
            border:'2px solid '+(plan==='basic'?'#2c5f8d':'#e2e8f0'),
            borderRadius:18, padding:'18px 18px', position:'relative',opacity:0.88}}>
            <div style={{position:'absolute',top:-10,right:16,background:'#718096',color:'#fff',
              fontSize:10,fontWeight:800,padding:'4px 10px',borderRadius:9999}}>곧 오픈</div>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:8}}>
              <div>
                <div style={{fontSize:11,fontWeight:800,color:'#2c5f8d',marginBottom:3,letterSpacing:.5,textTransform:'uppercase'}}>Basic 구독</div>
                <div style={{fontSize:17,fontWeight:800}}>매월 3회까지 분석</div>
              </div>
              <div style={{width:22,height:22,borderRadius:11,border:'2px solid '+(plan==='basic'?'#2c5f8d':'#cbd5e0'),
                background:plan==='basic'?'#2c5f8d':'#fff',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:11}}>
                {plan==='basic' && '✓'}
              </div>
            </div>
            <div style={{display:'flex',alignItems:'baseline',gap:8,marginBottom:10}}>
              <span style={{fontSize:24,fontWeight:800,color:'#1a1a2e'}}>월 34,000원</span>
              <span style={{fontSize:11,color:'#718096'}}>/ 월 3회</span>
            </div>
            <div style={{fontSize:12,color:'#4a5568',lineHeight:1.7}}>
              ✓ Haiku 4.5 AI 리서치<br/>
              ✓ 과거 리포트 비교 그래프
            </div>
          </div>

          <div onClick={()=>setPlan('pro')} style={{
            cursor:'pointer', background:'#fffbf5',
            border:'2px solid '+(plan==='pro'?'#ff9933':'#e6852b44'),
            borderRadius:18, padding:'18px 18px', position:'relative',opacity:0.88}}>
            <div style={{position:'absolute',top:-10,right:16,background:'#718096',color:'#fff',
              fontSize:10,fontWeight:800,padding:'4px 10px',borderRadius:9999}}>곧 오픈</div>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:8}}>
              <div>
                <div style={{fontSize:11,fontWeight:800,color:'#e6852b',marginBottom:3,letterSpacing:.5,textTransform:'uppercase'}}>Pro 구독</div>
                <div style={{fontSize:17,fontWeight:800}}>더 정교한 AI 리서치</div>
              </div>
              <div style={{width:22,height:22,borderRadius:11,border:'2px solid '+(plan==='pro'?'#ff9933':'#e6852b44'),
                background:plan==='pro'?'#ff9933':'#fff',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:11}}>
                {plan==='pro' && '✓'}
              </div>
            </div>
            <div style={{display:'flex',alignItems:'baseline',gap:8,marginBottom:10}}>
              <span style={{fontSize:24,fontWeight:800,color:'#1a1a2e'}}>월 58,000원</span>
              <span style={{fontSize:11,color:'#718096'}}>/ 월 3회</span>
            </div>
            <div style={{fontSize:12,color:'#4a5568',lineHeight:1.7}}>
              ✓ <b>Opus 4.7</b> 고정밀 AI 리서치<br/>
              ✓ 논문 5편 + 임상 사례 상세 분석
            </div>
          </div>
        </div>

        {!isSubscription && (
          <>
            <div className="hr-label">결제 방법</div>
            <div style={{display:'flex',gap:8}}>
              {PAY_METHODS.map((m) => (
                <div key={m.k} onClick={() => setPayMethod(m.k)}
                  style={{
                    flex:1,cursor:'pointer',
                    border:'2px solid '+(payMethod===m.k?'#2c5f8d':'#e2e8f0'),
                    background: payMethod===m.k ? '#2c5f8d10' : '#fff',
                    borderRadius:12,padding:'14px 8px',textAlign:'center',
                    fontWeight:700,fontSize:13,
                    color: payMethod===m.k ? '#2c5f8d' : '#4a5568',
                  }}>
                  <div style={{fontSize:22,marginBottom:4}}>{m.emoji}</div>
                  {m.lbl}
                </div>
              ))}
            </div>
            <div style={{marginTop:8,fontSize:11,color:'#a0aec0',textAlign:'center'}}>
              카카오페이는 곧 지원 예정이에요
            </div>
          </>
        )}

        {isSubscription && (
          <div style={{
            marginTop:20,padding:'14px 16px',borderRadius:12,
            background:'#f8f9fa',border:'1px dashed #cbd5e0',
            fontSize:12,color:'#4a5568',lineHeight:1.7,textAlign:'center'}}>
            구독 결제는 준비 중이에요. 우선 <b>1회 분석</b>으로 이용해보세요.
          </div>
        )}
      </div>

      <div style={{position:'absolute',bottom:0,left:0,right:0,padding:'16px 20px 34px',
        background:'linear-gradient(180deg,transparent,#fffbf5 40%)'}}>
        <button className="btn btn-secondary btn-block"
          onClick={handleSubmit}
          disabled={submitting || isSubscription}
          style={{opacity:(submitting||isSubscription)?0.55:1}}>
          {submitting ? '결제창 여는 중...' :
            isSubscription ? '구독 결제는 곧 준비됩니다' :
            `${amount.toLocaleString('ko-KR')}원 결제하고 분석 요청`}
        </button>
        {error && (
          <div style={{color:'#e74c3c',fontSize:12,marginTop:10,textAlign:'center',lineHeight:1.5}}>{error}</div>
        )}
        <div style={{marginTop:10,fontSize:11,color:'#718096',textAlign:'center',lineHeight:1.6}}>
          리포트는 7일 이내 전달 · 결제 후 환불은 안내를 따라주세요
        </div>
      </div>
    </div>
  );
}

// ————————————————————————————————————————————————————
// 5. Case status — polls DB, still offers a demo "advance" button
//                  until Phase 3 brings real operator workflow online.
// ————————————————————————————————————————————————————
const STATUS_COPY = {
  received: {
    title: '영상이 잘 도착했어요',
    body: '오늘 안에 분석이 시작돼요.\n결과는 이틀 안에 알림으로 보내드려요.',
  },
  analyzing: {
    title: '짱샘이 꼼꼼히 보고 있어요',
    body: '25년 임상 경험으로 영상을 봅니다.\n근거 자료도 함께 찾고 있어요.',
  },
  research: {
    title: 'AI가 근거 자료를 찾는 중이에요',
    body: '비슷한 사례의 논문과 자료를 모아\n리포트를 보강하고 있어요.',
  },
  reviewing: {
    title: '짱샘이 최종 검토 중이에요',
    body: '마지막으로 한 번 더 읽어보고 있어요.\n곧 전달드릴게요.',
  },
  done: {
    title: '리포트가 준비됐어요 💌',
    body: '아래에서 리포트를 열어보세요.',
  },
};

export function ScreenStatus({ caseRecord, onCaseUpdate, onBack, onDone }) {
  const [status, setStatus] = useState(caseRecord?.status ?? 'received');
  const [progress, setProgress] = useState(0);

  // Poll the case status every 5s
  useEffect(() => {
    if (!caseRecord?.id) return;
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const fresh = await getCaseById(caseRecord.id);
        if (cancelled || !fresh) return;
        setStatus(fresh.status);
        onCaseUpdate?.(fresh);
      } catch (err) {
        console.error('case poll failed:', err);
      }
    };
    fetchOnce();
    const t = setInterval(fetchOnce, 5000);
    return () => { cancelled = true; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseRecord?.id]);

  // Fake progress animation on 'analyzing' (cosmetic only)
  useEffect(() => {
    if (status !== 'analyzing' && status !== 'research') return;
    setProgress(0);
    const t = setInterval(() => setProgress((p) => Math.min(p + 1.5, 100)), 80);
    return () => clearInterval(t);
  }, [status]);

  const copy = STATUS_COPY[status] ?? STATUS_COPY.received;
  const stages = [
    { k:'received',   em:'📥', t:'접수 완료',            s:'운영진에게 전달됐어요' },
    { k:'analyzing',  em:'🔬', t:'영상을 보고 있어요',   s:'짱샘이 꼼꼼히 관찰 중' },
    { k:'research',   em:'📚', t:'근거 자료 리서치',     s:'AI가 관련 논문을 수집' },
    { k:'reviewing',  em:'✍️', t:'짱샘 최종 검토',       s:'편지 톤으로 정리 중' },
    { k:'done',       em:'💌', t:'리포트 준비 완료',     s:'이제 열어보실 수 있어요' },
  ];
  const currentIdx = stages.findIndex((st) => st.k === status);

  return (
    <div className="screen" style={{paddingBottom:100}}>
      <div className="status-spacer"/>
      <div style={{display:'flex',alignItems:'center',padding:'8px 8px 14px',gap:4}}>
        <button onClick={onBack}
          style={{display:'flex',alignItems:'center',gap:4,border:'none',background:'transparent',padding:'10px 12px',cursor:'pointer'}}>
          <span style={{fontSize:18}}>←</span>
          <span style={{fontSize:13,fontWeight:800}}>분석 진행 상황</span>
        </button>
      </div>

      <div style={{padding:'0 22px'}}>
        <div className="card" style={{textAlign:'center',paddingTop:28,paddingBottom:28,
          background:'linear-gradient(180deg,#fffbf5,#ffffff)'}}>
          <img src="/assets/character_jjangsaem.png" alt="" style={{width:110,height:110,borderRadius:'50%',objectFit:'cover',marginBottom:14}}/>
          <div style={{fontSize:19,fontWeight:800,letterSpacing:-0.3,marginBottom:4}}>
            {copy.title}
          </div>
          <div style={{fontSize:13,color:'#4a5568',lineHeight:1.7,whiteSpace:'pre-line'}}>
            {copy.body}
          </div>
        </div>

        <div style={{marginTop:22}}>
          {stages.map((st, i) => {
            const active = i === currentIdx;
            const passed = currentIdx > i;
            return (
              <div key={st.k} style={{display:'flex',gap:14,padding:'10px 0',opacity:(active||passed)?1:0.4}}>
                <div style={{width:36,height:36,borderRadius:18,
                  background: passed?'#27ae60':active?'#ff9933':'#e2e8f0',
                  color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',
                  fontSize:16,flexShrink:0, position:'relative'}}>
                  {passed ? '✓' : st.em}
                  {active && <span style={{position:'absolute',inset:-4,borderRadius:20,border:'2px solid #ff9933',animation:'blink 1.4s infinite'}}/>}
                </div>
                <div style={{flex:1}}>
                  <div style={{fontSize:14,fontWeight:700,color:'#1a1a2e'}}>{st.t}</div>
                  <div style={{fontSize:12,color:'#718096',marginTop:2}}>{st.s}</div>
                  {active && (status==='analyzing' || status==='research') && (
                    <div style={{marginTop:8,height:4,borderRadius:2,background:'#f3ece2',overflow:'hidden'}}>
                      <div style={{width:`${progress}%`,height:'100%',background:'#ff9933',transition:'width .3s'}}/>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {status === 'done' && (
          <button className="btn btn-primary btn-block" style={{marginTop:10}} onClick={onDone}>
            💌 리포트 열어보기
          </button>
        )}
      </div>
    </div>
  );
}

// ————————————————————————————————————————————————————
// 6. Report (letter + evidence hybrid)
// ————————————————————————————————————————————————————
export function ScreenReport({ caseRecord, onBack }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!caseRecord?.id) { setLoading(false); return; }
    let cancelled = false;
    supabase
      .from('it_reports')
      .select('case_id, letter, content, finalized_at, viewed_at')
      .eq('case_id', caseRecord.id)
      .maybeSingle()
      .then(async ({ data }) => {
        if (cancelled) return;
        setReport(data ?? null);
        setLoading(false);
        if (data && !data.viewed_at) {
          await supabase
            .from('it_reports')
            .update({ viewed_at: new Date().toISOString() })
            .eq('case_id', caseRecord.id);
        }
      });
    return () => { cancelled = true; };
  }, [caseRecord?.id]);

  const childName = caseRecord?.it_children?.name ?? '';
  const title = caseRecord?.concern_tags?.[0]
    ? `${caseRecord.concern_tags[0]} 관찰 리포트`
    : childName
      ? `${childName} 리포트`
      : '관찰 리포트';

  return (
    <div className="screen" style={{paddingBottom:90,background:'#fffbf5'}}>
      <div className="status-spacer"/>
      <div style={{display:'flex',alignItems:'center',padding:'8px 8px 14px',gap:4}}>
        <button onClick={onBack} style={{border:'none',background:'transparent',padding:'10px 12px',fontSize:18,cursor:'pointer'}}>←</button>
        <div style={{flex:1,fontSize:13,fontWeight:800}}>💌 {title}</div>
        <button style={{border:'none',background:'transparent',padding:'10px 12px',fontSize:16,cursor:'pointer'}}>⋯</button>
      </div>
      <ReportView caseRecord={caseRecord} report={report} loading={loading}/>
    </div>
  );
}

// Pure renderer used by both the parent app and the admin preview modal.
// Receives report data via props — does not touch supabase.
export function ReportView({ caseRecord, report, loading }) {
  const [tab, setTab] = useState('letter');

  const childName = caseRecord?.it_children?.name ?? '';
  const finalizedAt = report?.finalized_at
    ? new Date(report.finalized_at).toISOString().slice(0, 10).replace(/-/g, '.')
    : formatDate(new Date().toISOString());

  return (
    <>
      <div style={{padding:'0 20px 0'}}>
        <div style={{background:'linear-gradient(135deg,#2c5f8d,#3a7ab5)',borderRadius:20,
          padding:'22px 22px 20px',color:'#fff',position:'relative',overflow:'hidden'}}>
          <div style={{position:'absolute',right:-30,top:-30,fontSize:140,opacity:0.08}}>🧠</div>
          <div style={{fontSize:10,fontWeight:800,letterSpacing:1,opacity:.75,marginBottom:6}}>KIDSPHYSIO · 개인 리포트</div>
          <div style={{fontSize:19,fontWeight:800,letterSpacing:-0.3,lineHeight:1.35,marginBottom:4}}>
            {childName || '아이'}의 {caseRecord?.concern_tags?.[0] ?? '원시반사'},<br/>지금 이 시기에 본 것들
          </div>
          <div style={{fontSize:12,opacity:.85,marginTop:8,display:'flex',gap:10,alignItems:'center'}}>
            <span>👩‍⚕️ 짱샘</span>
            <span style={{opacity:.5}}>·</span>
            <span>📅 {finalizedAt}</span>
          </div>
          <div style={{display:'flex',gap:6,marginTop:12,flexWrap:'wrap'}}>
            {(caseRecord?.concern_tags ?? ['모로반사', '감각 통합']).slice(0, 3).map((t, i) => (
              <span key={i} className="pill" style={{background:'rgba(255,255,255,.2)',color:'#fff'}}>{t}</span>
            ))}
          </div>
        </div>
      </div>

      <div style={{display:'flex',gap:6,padding:'18px 20px 0'}}>
        {[
          {k:'letter',em:'💌',lbl:'짱샘 편지'},
          {k:'obs',em:'🔬',lbl:'관찰 요약'},
          {k:'refs',em:'📚',lbl:'근거 자료'},
        ].map(t=>(
          <div key={t.k} onClick={()=>setTab(t.k)} style={{
            flex:1,padding:'10px 0',borderRadius:9999,textAlign:'center',
            fontSize:12,fontWeight:700,cursor:'pointer',
            background: tab===t.k?'#2c5f8d':'#fff',
            color: tab===t.k?'#fff':'#4a5568',
            border:'1px solid '+(tab===t.k?'#2c5f8d':'#e2e8f0')}}>
            {t.em} {t.lbl}
          </div>
        ))}
      </div>

      <div style={{padding:'16px 20px 0'}}>
        {tab==='letter' && <ReportLetter report={report} loading={loading}/>}
        {tab==='obs' && <ReportObservations report={report}/>}
        {tab==='refs' && <ReportRefs report={report}/>}
      </div>

      <div style={{padding:'22px 20px 34px'}}>
        <button
          className="btn btn-primary btn-block"
          style={{marginBottom:10}}
          onClick={() => window.open('https://open.kakao.com/o/s3YnSoni', '_blank', 'noopener')}
        >
          💬 짱샘에게 질문하기
        </button>
        <button
          className="btn btn-outline btn-block"
          onClick={() => saveReportAsPdf({ caseRecord, report })}
          disabled={!report?.letter}
        >
          📄 PDF로 저장 · 공유
        </button>
      </div>
    </>
  );
}

// ────────────────────────────────────────────────────────────────────
// PDF export — builds a static HTML snapshot of the full report
// (header + letter + observations + recommendations + papers) and runs
// html2pdf.js (dynamically imported to keep the initial bundle small).
// ────────────────────────────────────────────────────────────────────
function escapeHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

async function saveReportAsPdf({ caseRecord, report }) {
  if (!report?.letter) {
    alert('아직 편지가 비어 있어요.');
    return;
  }

  const html2pdf = (await import('html2pdf.js')).default;

  const childName = caseRecord?.it_children?.name ?? '아이';
  const concernTag = caseRecord?.concern_tags?.[0] ?? '관찰';
  const finalizedAt = report?.finalized_at
    ? new Date(report.finalized_at).toISOString().slice(0, 10).replace(/-/g, '.')
    : '';
  const concernPills = (caseRecord?.concern_tags ?? []).slice(0, 3);
  const letterParas = stripMarkdown(report?.letter ?? '').split(/\n\s*\n/).filter(Boolean);

  // PDF에도 letter 단락 사이에 인포그래픽을 끼워 넣는다. signed URL은 1시간
  // 유효 — html2canvas는 useCORS=true와 Supabase의 ACAO:* 헤더 조합으로 그대로 그린다.
  const illustrations = report?.content?.illustrations ?? [];
  const illusEntries = await Promise.all(
    illustrations.map(async (r) => {
      if (!r?.storage_path) return null;
      try {
        const { data } = await supabase.storage
          .from('it-videos')
          .createSignedUrl(r.storage_path, 3600);
        if (!data?.signedUrl) return null;
        return { after_paragraph: r.after_paragraph, alt: r.alt ?? '', url: data.signedUrl };
      } catch { return null; }
    }),
  );
  const illusByPara = new Map();
  illusEntries.forEach((e) => {
    if (!e) return;
    const list = illusByPara.get(e.after_paragraph) ?? [];
    list.push(e);
    illusByPara.set(e.after_paragraph, list);
  });
  const observations = (report?.content?.observations ?? []).map((o) => ({
    ...o,
    t: stripMarkdown(o.t),
    ts: stripMarkdown(o.ts),
    tag: stripMarkdown(o.tag),
  }));
  const obsHeadline = stripMarkdown(report?.content?.observation_headline ?? '');
  const recs = (report?.content?.recommendations ?? []).map((r) => ({
    ...r,
    title: stripMarkdown(r.title ?? r.t),
    detail: stripMarkdown(r.detail ?? r.s),
  }));
  const papers = (report?.content?.papers ?? []).map((p) => ({
    ...p,
    title: stripMarkdown(p.title ?? p.t),
    source: stripMarkdown(p.source ?? p.src),
    quote: stripMarkdown(p.quote ?? p.q),
  }));

  const html = `
    <div style="font-family:-apple-system,BlinkMacSystemFont,'Apple SD Gothic Neo','Noto Sans KR',sans-serif;color:#1a1a2e;background:#fffbf5;padding:28px;width:560px;line-height:1.6;">
      <div style="background:linear-gradient(135deg,#2c5f8d,#3a7ab5);border-radius:18px;padding:22px;color:#fff;margin-bottom:22px;">
        <div style="font-size:11px;font-weight:800;letter-spacing:1px;opacity:.75;margin-bottom:6px;">KIDSPHYSIO · 개인 리포트</div>
        <div style="font-size:20px;font-weight:800;line-height:1.35;margin-bottom:8px;">${escapeHtml(childName)}의 ${escapeHtml(concernTag)},<br/>지금 이 시기에 본 것들</div>
        <div style="font-size:12px;opacity:.85;">짱샘 · ${escapeHtml(finalizedAt)}</div>
        ${concernPills.length ? `<div style="margin-top:10px;">${concernPills.map(t => `<span style="display:inline-block;background:rgba(255,255,255,.2);color:#fff;padding:4px 10px;border-radius:9999px;font-size:11px;font-weight:700;margin-right:6px;">${escapeHtml(t)}</span>`).join('')}</div>` : ''}
      </div>

      <h2 style="font-size:15px;font-weight:800;margin:0 0 10px;">짱샘 편지</h2>
      <div style="background:#fffdf8;border:1px solid #f0e8d8;border-radius:16px;padding:20px;margin-bottom:22px;">
        ${letterParas.map((p, i) => {
          const paraHtml = `<p style="font-size:13px;line-height:1.85;margin:0 0 12px;white-space:pre-line;">${escapeHtml(p)}</p>`;
          const after = illusByPara.get(i + 1) ?? [];
          const imgsHtml = after.map((e) => `
            <figure style="margin:14px 0 18px;border-radius:12px;overflow:hidden;background:#f1f5f9;page-break-inside:avoid;">
              <img src="${escapeHtml(e.url)}" alt="${escapeHtml(e.alt)}" style="display:block;width:100%;height:auto;" crossorigin="anonymous"/>
              ${e.alt ? `<figcaption style="font-size:11px;color:#718096;padding:8px 12px;text-align:center;line-height:1.5;background:#fff;">${escapeHtml(e.alt)}</figcaption>` : ''}
            </figure>
          `).join('');
          return paraHtml + imgsHtml;
        }).join('')}
        <div style="font-size:12px;color:#718096;margin-top:14px;border-top:1px solid #f0e8d8;padding-top:12px;"><b>짱샘</b> 드림${finalizedAt ? ` · ${escapeHtml(finalizedAt)}` : ''}<br/>키즈피지오 장지예</div>
      </div>

      ${observations.length || obsHeadline ? `
        <h2 style="font-size:15px;font-weight:800;margin:0 0 10px;">관찰 요약</h2>
        <div style="background:#fff;border:1px solid #eee;border-radius:14px;padding:18px;margin-bottom:18px;page-break-inside:avoid;">
          ${obsHeadline ? `<div style="font-size:14px;font-weight:700;line-height:1.5;margin-bottom:12px;">${escapeHtml(obsHeadline)}</div>` : ''}
          ${observations.map(o => `
            <div style="margin-bottom:12px;padding-left:14px;border-left:3px solid ${o.tone === 'warm' ? '#ff9933' : '#2c5f8d'};">
              ${o.ts ? `<div style="font-size:11px;font-weight:700;color:${o.tone === 'warm' ? '#e6852b' : '#2c5f8d'};">${escapeHtml(o.ts)}</div>` : ''}
              <div style="font-size:13px;margin-top:2px;line-height:1.5;">${escapeHtml(o.t)}</div>
              ${o.tag ? `<span style="display:inline-block;margin-top:4px;background:${o.tone === 'warm' ? '#ff993322' : '#27ae6018'};color:${o.tone === 'warm' ? '#e6852b' : '#1f7a3f'};font-size:10px;font-weight:700;padding:3px 8px;border-radius:9999px;">${escapeHtml(o.tag)}</span>` : ''}
            </div>
          `).join('')}
        </div>
      ` : ''}

      ${recs.length ? `
        <h3 style="font-size:13px;font-weight:800;margin:0 0 10px;color:#4a5568;">이번 달 권유</h3>
        <div style="margin-bottom:24px;">
          ${recs.map(r => `
            <div style="display:flex;gap:12px;padding:10px 12px;background:#fffbf5;border-radius:12px;margin-bottom:8px;page-break-inside:avoid;">
              <div style="width:34px;height:34px;border-radius:10px;background:#fff;display:flex;align-items:center;justify-content:center;font-size:17px;flex-shrink:0;">${escapeHtml(r.emoji ?? r.em ?? '')}</div>
              <div style="flex:1;">
                <div style="font-size:13px;font-weight:700;">${escapeHtml(r.title ?? r.t ?? '')}</div>
                <div style="font-size:11px;color:#718096;margin-top:2px;line-height:1.5;">${escapeHtml(r.detail ?? r.s ?? '')}</div>
              </div>
            </div>
          `).join('')}
        </div>
      ` : ''}

      ${papers.length ? `
        <h2 style="font-size:15px;font-weight:800;margin:0 0 10px;">근거 자료</h2>
        ${papers.map(p => `
          <div style="background:#fff;border:1px solid #eee;border-radius:12px;padding:14px;margin-bottom:10px;page-break-inside:avoid;">
            <div style="font-size:13px;font-weight:700;line-height:1.4;margin-bottom:6px;">${escapeHtml(p.title ?? p.t ?? '')}</div>
            <div style="font-size:11px;color:#718096;margin-bottom:8px;">${escapeHtml(p.source ?? p.src ?? '')}</div>
            <div style="font-size:12px;line-height:1.65;background:#f9fafb;padding:10px;border-radius:8px;">"${escapeHtml(p.quote ?? p.q ?? '')}"</div>
            ${p.url ? `<div style="margin-top:8px;font-size:10px;color:#2c5f8d;word-break:break-all;">${escapeHtml(p.url)}</div>` : ''}
          </div>
        `).join('')}
        <div style="margin-top:12px;padding:12px;background:#fef9f0;border-radius:10px;font-size:11px;color:#4a5568;line-height:1.65;">⚠️ 이 자료들은 참고 문헌이에요. 진단을 대체하지 않아요.</div>
      ` : ''}
    </div>
  `;

  const wrapper = document.createElement('div');
  wrapper.style.cssText = 'position:fixed;left:-9999px;top:0;';
  wrapper.innerHTML = html;
  document.body.appendChild(wrapper);

  const filename = `${childName}-${concernTag}-리포트.pdf`;

  try {
    const blob = await html2pdf()
      .set({
        margin: [10, 0, 10, 0],
        filename,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true, logging: false, backgroundColor: '#fffbf5' },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['css', 'legacy'] },
      })
      .from(wrapper.firstElementChild)
      .outputPdf('blob');

    const file = new File([blob], filename, { type: 'application/pdf' });

    // Mobile-only: native share sheet (iOS 15+/Android Chrome). PC에서는 Web
    // Share API가 트리거되면 Windows 공유 시트가 떠 어색해서, 데스크톱에선
    // 항상 일반 다운로드로 떨군다.
    if (
      isMobileLike() &&
      typeof navigator !== 'undefined' &&
      typeof navigator.canShare === 'function' &&
      navigator.canShare({ files: [file] }) &&
      typeof navigator.share === 'function'
    ) {
      try {
        await navigator.share({
          files: [file],
          title: filename,
          text: `${childName} 분석 리포트`,
        });
        return;
      } catch (err) {
        if (err?.name === 'AbortError') return; // user dismissed sheet
        // Other errors (e.g. share denied) → fall through to download
      }
    }

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (err) {
    console.error('PDF export failed:', err);
    alert('PDF 저장에 실패했어요. 잠시 후 다시 시도해 주세요.');
  } finally {
    wrapper.remove();
  }
}

function ReportLetter({ report, loading }) {
  // Letter 단락 사이에 끼울 인포그래픽. it_reports.content.illustrations에
  // [{ after_paragraph, alt, storage_path }] 형식으로 저장돼 있다. signed URL은
  // 부모 RLS가 허용하므로 클라이언트에서 바로 발급받는다.
  const illustrations = report?.content?.illustrations ?? [];
  const illusKey = illustrations
    .map((r) => r?.storage_path)
    .filter(Boolean)
    .join('|');
  const [illusUrls, setIllusUrls] = useState({});

  useEffect(() => {
    if (!illusKey) { setIllusUrls({}); return; }
    let cancelled = false;
    (async () => {
      const next = {};
      await Promise.all(illustrations.map(async (r) => {
        if (!r?.storage_path) return;
        try {
          const { data } = await supabase.storage
            .from('it-videos')
            .createSignedUrl(r.storage_path, 3600);
          if (data?.signedUrl) next[r.storage_path] = data.signedUrl;
        } catch { /* skip — silently fall back to no image */ }
      }));
      if (!cancelled) setIllusUrls(next);
    })();
    return () => { cancelled = true; };
    // illusKey changes whenever the set of storage paths changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [illusKey]);

  if (loading) {
    return (
      <div className="letter" style={{textAlign:'center',padding:'40px 20px'}}>
        <div className="spinner" style={{display:'inline-block'}}/>
      </div>
    );
  }

  // Real letter from operator (Phase 3) — render as paragraphs split on \n\n
  if (report?.letter) {
    const paragraphs = stripMarkdown(report.letter).split(/\n\s*\n/).filter(Boolean);
    return (
      <div className="letter">
        <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:14}}>
          <img src="/assets/character_jjangsaem.png" alt="" style={{width:44,height:44,borderRadius:22,objectFit:'cover'}}/>
          <div>
            <div style={{fontSize:13,fontWeight:800}}>짱샘 드림</div>
            <div style={{fontSize:11,color:'#718096'}}>소아물리치료사 · 25년차</div>
          </div>
        </div>
        {paragraphs.map((p, i) => {
          // after_paragraph는 1-based, paragraph index i는 0-based.
          const after = illustrations.filter((r) => r?.after_paragraph === i + 1);
          return (
            <Fragment key={i}>
              <p style={{whiteSpace:'pre-line'}}>{p}</p>
              {after.map((r, j) => {
                const url = r?.storage_path ? illusUrls[r.storage_path] : null;
                if (!url) return null;
                return (
                  <figure key={`illus-${i}-${j}`} style={{
                    margin:'14px 0 18px',
                    borderRadius:14,
                    overflow:'hidden',
                    background:'#f1f5f9',
                  }}>
                    <img src={url} alt={r.alt ?? ''}
                      style={{display:'block',width:'100%',height:'auto'}}/>
                    {r.alt && (
                      <figcaption style={{
                        fontSize:11,color:'#718096',padding:'8px 12px',
                        textAlign:'center',lineHeight:1.5,background:'#fff',
                      }}>
                        {stripMarkdown(r.alt)}
                      </figcaption>
                    )}
                  </figure>
                );
              })}
            </Fragment>
          );
        })}
        <div className="sig">
          <b>짱샘</b> 드림 · {report.finalized_at ? formatDate(report.finalized_at) : ''}<br/>
          키즈피지오 장지예
        </div>
      </div>
    );
  }

  // No letter yet
  return (
    <div className="letter" style={{textAlign:'center',padding:'40px 20px'}}>
      <div style={{fontSize:40,marginBottom:10}}>💌</div>
      <div style={{fontSize:14,fontWeight:700,color:'#1a1a2e',marginBottom:4}}>
        편지가 아직 준비되지 않았어요
      </div>
      <div style={{fontSize:12,color:'#718096',lineHeight:1.7}}>
        짱샘이 영상을 보고 정리하는 중이에요.<br/>
        준비되면 알림으로 알려드릴게요.
      </div>
    </div>
  );
}

function ReportObservations({ report }) {
  const obs = report?.content?.observations ?? [];
  const obsHeadline = report?.content?.observation_headline ?? '';
  const recs = report?.content?.recommendations ?? [];

  if (obs.length === 0 && recs.length === 0) {
    return (
      <div style={{textAlign:'center',padding:'40px 20px'}}>
        <div style={{fontSize:40,marginBottom:10}}>🔬</div>
        <div style={{fontSize:14,fontWeight:700,color:'#1a1a2e',marginBottom:4}}>
          관찰 요약이 아직 준비되지 않았어요
        </div>
        <div style={{fontSize:12,color:'#718096',lineHeight:1.7}}>
          짱샘이 영상을 보고 정리하는 중이에요.
        </div>
      </div>
    );
  }

  return (
    <div>
      {obs.length > 0 && (
        <div className="card" style={{marginBottom:14}}>
          <div className="sec-lbl">핵심 관찰</div>
          {obsHeadline && (
            <div style={{fontSize:14,fontWeight:700,color:'#1a1a2e',lineHeight:1.5,marginBottom:10}}>
              {stripMarkdown(obsHeadline)}
            </div>
          )}
          <div className="tl">
            {obs.map((o,i)=>(
              <div key={i} className={'tl-node'+(o.tone==='warm'?' warm':'')}>
                <div style={{fontSize:11,fontWeight:700,color: o.tone==='warm'?'#e6852b':'#2c5f8d'}}>{o.ts}</div>
                <div style={{fontSize:13,color:'#1a1a2e',marginTop:2,lineHeight:1.5}}>{stripMarkdown(o.t)}</div>
                {o.tag && (
                  <span className="pill" style={{marginTop:4,
                    background:o.tone==='warm'?'#ff993322':'#27ae6018',
                    color:o.tone==='warm'?'#e6852b':'#1f7a3f'}}>
                    {stripMarkdown(o.tag)}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {recs.length > 0 && (
        <div className="card">
          <div className="sec-lbl">이번 달 권유</div>
          <div style={{display:'flex',flexDirection:'column',gap:10,marginTop:4}}>
            {recs.map((r,i)=>(
              <div key={i} style={{display:'flex',gap:12,padding:'10px 12px',background:'#fffbf5',borderRadius:12}}>
                <div style={{width:36,height:36,borderRadius:10,background:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontSize:17}}>{r.emoji ?? r.em}</div>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,fontWeight:700}}>{stripMarkdown(r.title ?? r.t)}</div>
                  <div style={{fontSize:11,color:'#718096',marginTop:2}}>{stripMarkdown(r.detail ?? r.s)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ReportRefs({ report }) {
  const realPapers = report?.content?.papers;
  const papers = (realPapers && realPapers.length > 0)
    ? realPapers.map((p) => ({
        cover: p.cover, year: p.year,
        t: stripMarkdown(p.title),
        src: stripMarkdown(p.source),
        q: stripMarkdown(p.quote),
        url: p.url,
      }))
    : [];

  if (papers.length === 0) {
    return (
      <div style={{textAlign:'center',padding:'40px 20px'}}>
        <div style={{fontSize:40,marginBottom:10}}>📚</div>
        <div style={{fontSize:14,fontWeight:700,color:'#1a1a2e',marginBottom:4}}>
          참고 자료가 아직 준비되지 않았어요
        </div>
        <div style={{fontSize:12,color:'#718096',lineHeight:1.7}}>
          짱샘이 관련 논문을 정리하는 중이에요.
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{fontSize:12,color:'#718096',marginBottom:12,lineHeight:1.6}}>
        🔬 짱샘이 이번 리포트를 쓰며 참고한 자료예요.
        논문 원문은 하단 링크에서 열어보실 수 있어요.
      </div>
      <div style={{display:'flex',flexDirection:'column',gap:10}}>
        {papers.map((p,i)=>(
          <div key={i} className="paper">
            <div className="cover">
              <div style={{textAlign:'center',lineHeight:1.2}}>{p.cover}<br/><span style={{opacity:.7,fontSize:9}}>{p.year}</span></div>
            </div>
            <div style={{flex:1,minWidth:0}}>
              <h5>{p.t}</h5>
              <div className="src">{p.src}</div>
              <div className="quote">"{p.q}"</div>
              {p.url && (
                <a href={p.url} target="_blank" rel="noreferrer"
                  style={{display:'inline-block',marginTop:8,fontSize:11,color:'#2c5f8d',fontWeight:700,textDecoration:'none'}}>
                  원문 열기 →
                </a>
              )}
            </div>
          </div>
        ))}
      </div>

      <div style={{marginTop:14,padding:14,background:'#fef9f0',borderRadius:12,fontSize:11,color:'#4a5568',lineHeight:1.65}}>
        ⚠️ 이 자료들은 <b>참고 문헌</b>이에요. 진단을 대체하지 않아요.
        증상이 심하거나 걱정이 크다면 가까운 소아재활의학과 방문을 권합니다.
      </div>
    </div>
  );
}

// ————————————————————————————————————————————————————
// 7. Notifications inbox
// ————————————————————————————————————————————————————
export function ScreenNotifications({
  notifications,
  userId,
  onOpenCase,
  onChange,
}) {
  const [busy, setBusy] = useState(false);

  const anyUnread = notifications.some((n) => !n.read_at);

  const handleClick = async (notif) => {
    setBusy(true);
    try {
      if (!notif.read_at) {
        await markNotificationRead(notif.id);
        onChange?.();
      }
      if (notif.case_id) {
        onOpenCase?.(notif.case_id);
      }
    } catch (err) {
      alert("알림 처리 실패: " + (err.message ?? "unknown"));
    } finally {
      setBusy(false);
    }
  };

  const handleMarkAll = async () => {
    if (!userId) return;
    setBusy(true);
    try {
      await markAllNotificationsRead(userId);
      onChange?.();
    } catch (err) {
      alert("처리 실패: " + (err.message ?? "unknown"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="screen" style={{paddingBottom:90}}>
      <div className="status-spacer"/>
      <div style={{display:'flex',alignItems:'center',padding:'14px 20px',gap:4}}>
        <div style={{fontSize:17,fontWeight:800,flex:1,letterSpacing:-0.3}}>🔔 알림</div>
        {anyUnread && (
          <button onClick={handleMarkAll} disabled={busy}
            style={{border:'none',background:'transparent',padding:'10px 4px',fontSize:11,color:'#2c5f8d',fontWeight:700,cursor:'pointer'}}>
            모두 읽음
          </button>
        )}
      </div>

      <div style={{padding:'0 20px'}}>
        {notifications.length === 0 ? (
          <div style={{textAlign:'center',padding:'80px 20px'}}>
            <div style={{fontSize:40,marginBottom:10}}>🔔</div>
            <div style={{fontSize:14,fontWeight:700,color:'#1a1a2e',marginBottom:4}}>
              아직 알림이 없어요
            </div>
            <div style={{fontSize:12,color:'#718096',lineHeight:1.7}}>
              리포트가 준비되면 여기로 알려드릴게요.
            </div>
          </div>
        ) : (
          <div style={{display:'flex',flexDirection:'column',gap:10}}>
            {notifications.map((n) => {
              const unread = !n.read_at;
              const emoji = NOTIF_EMOJI[n.kind] ?? "🔔";
              return (
                <div key={n.id}
                  onClick={() => handleClick(n)}
                  style={{
                    padding:'14px 16px',
                    background: unread ? '#fffbf5' : '#fff',
                    border: '1px solid ' + (unread ? '#ff993344' : '#e2e8f0'),
                    borderLeft: unread ? '3px solid #ff9933' : '1px solid #e2e8f0',
                    borderRadius: 14,
                    cursor: 'pointer',
                    opacity: busy ? 0.5 : 1,
                  }}>
                  <div style={{display:'flex',alignItems:'flex-start',gap:10}}>
                    <div style={{fontSize:18,lineHeight:1.2,marginTop:1}}>{emoji}</div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:13,fontWeight:unread?800:700,color:'#1a1a2e',lineHeight:1.4,marginBottom:2}}>
                        {n.title}
                      </div>
                      {n.body && (
                        <div style={{fontSize:12,color:'#4a5568',lineHeight:1.55,marginBottom:4}}>
                          {n.body}
                        </div>
                      )}
                      <div style={{fontSize:10,color:'#718096'}}>{timeAgo(n.created_at)}</div>
                    </div>
                    {unread && (
                      <div style={{width:8,height:8,borderRadius:4,background:'#ff9933',flexShrink:0,marginTop:6}}/>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ————————————————————————————————————————————————————
// 7. Settings — profile, credits (분석권), children edit, logout
// ————————————————————————————————————————————————————

function ChildEditModal({ child, onClose, onSave }) {
  const [name, setName] = useState(child?.name ?? "");
  // birth_month is stored as a date like "2024-05-01"; <input type="month">
  // wants "YYYY-MM". Convert both ways.
  const initMonth = child?.birth_month ? String(child.birth_month).slice(0, 7) : "";
  const [birthMonth, setBirthMonth] = useState(initMonth);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async () => {
    if (!name.trim()) {
      setErr("이름을 입력해 주세요");
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      await onSave({
        name: name.trim(),
        birthMonth: birthMonth ? `${birthMonth}-01` : null,
      });
    } catch (e) {
      setErr(e.message ?? "저장에 실패했어요");
      setSaving(false);
    }
  };

  return (
    <div onClick={() => !saving && onClose()}
      style={{position:'fixed',inset:0,background:'rgba(15,23,42,.45)',
        display:'flex',alignItems:'center',justifyContent:'center',zIndex:120,padding:20}}>
      <div onClick={(e)=>e.stopPropagation()}
        style={{width:'100%',maxWidth:380,background:'#fff',borderRadius:16,padding:22,
          boxShadow:'0 20px 60px rgba(0,0,0,.2)'}}>
        <div style={{fontSize:17,fontWeight:800,marginBottom:14}}>아이 정보 수정</div>
        <label style={{display:'block',marginBottom:14}}>
          <span style={{fontSize:11,fontWeight:700,color:'#4a5568'}}>이름</span>
          <input type="text" value={name} onChange={(e)=>setName(e.target.value)}
            placeholder="아이 이름"
            style={{width:'100%',marginTop:6,padding:'12px 14px',border:'1px solid #e2e8f0',
              borderRadius:10,fontSize:14,fontFamily:'inherit'}}/>
        </label>
        <label style={{display:'block',marginBottom:14}}>
          <span style={{fontSize:11,fontWeight:700,color:'#4a5568'}}>생년월</span>
          <input type="month" value={birthMonth} onChange={(e)=>setBirthMonth(e.target.value)}
            max={new Date().toISOString().slice(0,7)}
            style={{width:'100%',marginTop:6,padding:'12px 14px',border:'1px solid #e2e8f0',
              borderRadius:10,fontSize:14,fontFamily:'inherit'}}/>
        </label>
        {err && (
          <div style={{padding:'10px 12px',background:'#e74c3c15',color:'#c0392b',
            borderRadius:10,fontSize:12,marginBottom:12}}>{err}</div>
        )}
        <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
          <button onClick={onClose} disabled={saving}
            style={{padding:'10px 18px',borderRadius:10,background:'#fff',
              border:'1px solid #e2e8f0',fontSize:13,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>
            취소
          </button>
          <button onClick={submit} disabled={saving}
            style={{padding:'10px 18px',borderRadius:10,background:'#2c5f8d',
              border:'1px solid #2c5f8d',color:'#fff',fontSize:13,fontWeight:700,
              cursor:saving?'wait':'pointer',opacity:saving?0.7:1,fontFamily:'inherit'}}>
            {saving ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function ScreenSettings({
  profile,
  session,
  children,
  creditBalance = 0,
  onBuyPackage,
  onOpenHistory,
  onUpdateChild,
  onSignOut,
}) {
  const email = session?.user?.email ?? "";
  const displayName = profile?.display_name || (email ? email.split("@")[0] : "부모님");
  const initial = (displayName || "엄").trim().slice(0, 1);

  const [editingChild, setEditingChild] = useState(null);

  const handleLogout = () => {
    if (window.confirm("로그아웃 하시겠어요?")) onSignOut?.();
  };

  const handleSaveChild = async (patch) => {
    if (!editingChild) return;
    await onUpdateChild?.(editingChild.id, patch);
    setEditingChild(null);
  };

  return (
    <div className="screen" style={{paddingBottom:90}}>
      <div className="status-spacer"/>
      <div style={{padding:'14px 20px'}}>
        <div style={{fontSize:17,fontWeight:800,letterSpacing:-0.3}}>⚙️ 설정</div>
      </div>

      {/* Profile card */}
      <div style={{padding:'0 20px'}}>
        <div className="card" style={{display:'flex',alignItems:'center',gap:14}}>
          <div style={{width:48,height:48,borderRadius:24,background:'#ff9933',color:'#fff',
            display:'flex',alignItems:'center',justifyContent:'center',fontSize:18,fontWeight:800}}>
            {initial}
          </div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:15,fontWeight:800,color:'#1a1a2e'}}>{displayName}</div>
            <div style={{fontSize:12,color:'#718096',marginTop:2,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
              {email}
            </div>
          </div>
        </div>
      </div>

      {/* Credits section (분석권) */}
      <div style={{padding:'18px 20px 0'}}>
        <div className="sec-lbl">분석권</div>
        <div className="card">
          <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:12}}>
            <span className="pill pill-soft" style={{fontSize:12,padding:'5px 12px'}}>
              🎟 잔여 {creditBalance}회
            </span>
          </div>
          <div style={{fontSize:12,color:'#4a5568',lineHeight:1.7,marginBottom:14}}>
            {creditBalance > 0
              ? `현재 ${creditBalance}회의 분석권이 남아 있어요. 한 번에 영상 5~6개 분석 요청을 보낼 수 있어요.`
              : '잔여 분석권이 없어요. 패키지를 충전하면 영상 분석 요청을 다시 보낼 수 있어요.'}
          </div>
          <button onClick={onBuyPackage} className="btn btn-secondary btn-block">
            {creditBalance > 0 ? '분석권 추가 충전' : '분석권 구매'}
          </button>
          {onOpenHistory && (
            <button
              onClick={onOpenHistory}
              style={{
                width:'100%',marginTop:8,padding:'10px 14px',background:'#fff',
                border:'1px solid #e2e8f0',borderRadius:10,fontSize:13,fontWeight:700,
                color:'#2c5f8d',cursor:'pointer',fontFamily:'inherit',
              }}>
              🧾 결제 내역 보기
            </button>
          )}
        </div>
      </div>

      {/* Children section */}
      {children && children.length > 0 && (
        <div style={{padding:'18px 20px 0'}}>
          <div className="sec-lbl">등록된 아이</div>
          <div style={{display:'flex',flexDirection:'column',gap:10}}>
            {children.map((c) => (
              <div key={c.id} className="card-flat"
                onClick={() => setEditingChild(c)}
                style={{display:'flex',alignItems:'center',gap:12,cursor:'pointer'}}>
                <div className="char-round" style={{display:'flex',alignItems:'center',justifyContent:'center',fontSize:20}}>
                  {c.avatar_url
                    ? <img src={c.avatar_url} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>
                    : '👶'}
                </div>
                <div style={{flex:1}}>
                  <div style={{fontWeight:700,fontSize:13}}>{c.name}</div>
                  {c.birth_month && (
                    <div style={{fontSize:11,color:'#718096',marginTop:1}}>{formatAge(c.birth_month)}</div>
                  )}
                </div>
                <span style={{fontSize:18,color:'#a0aec0',fontWeight:700}}>›</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Logout */}
      <div style={{padding:'28px 20px 20px'}}>
        <button onClick={handleLogout}
          className="btn btn-ghost btn-block"
          style={{border:'1px solid #e2e8f0',background:'#fff',color:'#e74c3c'}}>
          로그아웃
        </button>
        <div style={{marginTop:18,fontSize:11,color:'#a0aec0',textAlign:'center',lineHeight:1.6}}>
          키즈피지오 장지예 · 짱샘의 책방<br/>
          i-talk · Phase 5
        </div>
      </div>

      {editingChild && (
        <ChildEditModal
          child={editingChild}
          onClose={() => setEditingChild(null)}
          onSave={handleSaveChild}
        />
      )}
    </div>
  );
}

// ————————————————————————————————————————————————————
// 5. PurchaseHistory — 내 결제 내역
//    it_credit_packages × it_purchases 조인. 무료권·환불·만료도 같이 표시.
// ————————————————————————————————————————————————————

function packageLabel(pkg) {
  const t = pkg.package_type;
  if (t?.startsWith("pkg")) {
    const n = parseInt(t.slice(3), 10);
    return Number.isFinite(n) ? `분석권 ${n}회 패키지` : "분석권 패키지";
  }
  if (t?.startsWith("free")) {
    const n = parseInt(t.slice(4), 10);
    return Number.isFinite(n) ? `무료 분석권 ${n}회` : "무료 분석권";
  }
  if (t === "once") return "1회 영상 분석";
  return t ?? "분석권";
}

function packageStatus(pkg) {
  const purchase = pkg.purchase;
  const refunded = purchase?.status === "refunded";
  const isFree = !purchase;
  const expired = pkg.expires_at && new Date(pkg.expires_at) < new Date();
  const exhausted = (pkg.credits_remaining ?? 0) <= 0;

  if (refunded) return { label: "환불 완료", bg: "#e74c3c12", fg: "#c0392b" };
  if (expired) return { label: "만료", bg: "#a0aec022", fg: "#4a5568" };
  if (exhausted) return { label: "소진", bg: "#a0aec022", fg: "#4a5568" };
  if (isFree) return { label: "무료", bg: "#fef5e7", fg: "#b7791f" };
  return { label: "사용 중", bg: "#2c5f8d12", fg: "#2c5f8d" };
}

function formatYMD(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")}`;
}

function formatWon(n) {
  if (n == null) return "—";
  return n.toLocaleString("ko-KR") + "원";
}

export function ScreenPurchaseHistory({ session, creditBalance = 0, onBack, onBuyPackage }) {
  const userId = session?.user?.id;
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [receiptBusyId, setReceiptBusyId] = useState(null);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        const list = await listMyPurchases(userId);
        if (!cancelled) setItems(list);
      } catch (err) {
        if (!cancelled) setError(err?.message ?? "내역을 불러올 수 없어요");
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  const handleReceipt = async (paymentKey, rowId) => {
    if (!paymentKey) return;
    setReceiptBusyId(rowId);
    try {
      const url = await fetchReceiptUrl(paymentKey);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setError(err?.message ?? "영수증을 불러올 수 없어요");
    } finally {
      setReceiptBusyId(null);
    }
  };

  return (
    <div className="screen" style={{paddingBottom:120}}>
      <div className="status-spacer"/>
      <div style={{display:'flex',alignItems:'center',padding:'8px 8px 14px',gap:4}}>
        <button onClick={onBack}
          style={{display:'flex',alignItems:'center',gap:4,border:'none',background:'transparent',padding:'10px 12px',cursor:'pointer'}}>
          <span style={{fontSize:18}}>←</span>
          <span style={{fontSize:12,color:'#718096',fontWeight:700}}>내 결제 내역</span>
        </button>
      </div>

      <div style={{padding:'0 22px 12px'}}>
        <div style={{fontSize:22,fontWeight:800,letterSpacing:-0.3,lineHeight:1.3,marginBottom:6}}>
          내 결제 내역
        </div>
        <div style={{fontSize:12,color:'#718096'}}>
          🎟 현재 잔여 분석권 <b style={{color:'#2c5f8d'}}>{creditBalance}회</b>
        </div>
      </div>

      {error && (
        <div style={{margin:'0 22px 12px',padding:'10px 12px',background:'#e74c3c15',color:'#c0392b',borderRadius:10,fontSize:12}}>
          {error}
        </div>
      )}

      <div style={{padding:'8px 20px 24px',display:'flex',flexDirection:'column',gap:10}}>
        {items === null && !error && (
          <div style={{display:'flex',justifyContent:'center',padding:'40px 0'}}>
            <div className="spinner"/>
          </div>
        )}
        {items && items.length === 0 && (
          <div className="card" style={{textAlign:'center',padding:'28px 16px',color:'#718096',fontSize:13,lineHeight:1.7}}>
            아직 분석권 구매 내역이 없어요.<br/>
            분석권을 충전하면 영상 분석 요청을 보낼 수 있어요.
          </div>
        )}
        {items && items.map((pkg) => {
          const purchase = pkg.purchase;
          const status = packageStatus(pkg);
          const dateIso = purchase?.paid_at || pkg.created_at;
          const showReceipt =
            purchase &&
            purchase.payment_provider === "toss" &&
            purchase.payment_ref &&
            purchase.status !== "failed";
          return (
            <div key={pkg.id} className="card" style={{display:'flex',flexDirection:'column',gap:10}}>
              <div style={{display:'flex',alignItems:'flex-start',gap:10}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:14,fontWeight:800,color:'#1a1a2e',lineHeight:1.4}}>
                    {packageLabel(pkg)}
                  </div>
                  <div style={{fontSize:11,color:'#718096',marginTop:3}}>
                    {formatYMD(dateIso)}
                    {purchase?.method && <> · {purchase.method}</>}
                  </div>
                </div>
                <span style={{
                  fontSize:11,fontWeight:700,padding:'4px 10px',borderRadius:9999,
                  background:status.bg,color:status.fg,whiteSpace:'nowrap',
                }}>
                  {status.label}
                </span>
              </div>

              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',
                fontSize:12,borderTop:'1px solid #f1ece4',paddingTop:10}}>
                <span style={{color:'#4a5568'}}>결제 금액</span>
                <span style={{fontWeight:700,color:'#1a1a2e'}}>
                  {purchase ? formatWon(purchase.amount_won) : "—"}
                </span>
              </div>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',fontSize:12}}>
                <span style={{color:'#4a5568'}}>잔여 / 총</span>
                <span style={{fontWeight:700,color: pkg.credits_remaining > 0 ? '#2c5f8d' : '#718096'}}>
                  {pkg.credits_remaining} / {pkg.credits_total}회
                </span>
              </div>
              {pkg.expires_at && (
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',fontSize:12}}>
                  <span style={{color:'#4a5568'}}>유효기간</span>
                  <span style={{fontWeight:600,color:'#718096'}}>
                    {formatYMD(pkg.expires_at)}까지
                  </span>
                </div>
              )}
              {purchase?.refunded_at && (
                <div style={{fontSize:11,color:'#c0392b',background:'#e74c3c10',
                  padding:'8px 10px',borderRadius:8,lineHeight:1.5}}>
                  {formatYMD(purchase.refunded_at)} 환불됨
                  {purchase.refund_reason && <> · {purchase.refund_reason}</>}
                </div>
              )}

              {showReceipt && (
                <button
                  onClick={() => handleReceipt(purchase.payment_ref, pkg.id)}
                  disabled={receiptBusyId === pkg.id}
                  style={{
                    border:'1px solid #e2e8f0',background:'#fff',color:'#2c5f8d',
                    padding:'8px 12px',borderRadius:8,fontSize:12,fontWeight:700,
                    cursor: receiptBusyId === pkg.id ? 'wait' : 'pointer',
                    fontFamily:'inherit',
                  }}>
                  {receiptBusyId === pkg.id ? '영수증 여는 중…' : '🧾 영수증 보기'}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div style={{padding:'0 20px 28px'}}>
        <button onClick={onBuyPackage} className="btn btn-secondary btn-block">
          분석권 추가 충전
        </button>
      </div>
    </div>
  );
}
